// @ts-nocheck
// Ported byte-identically from v1 views/dashboard/CallCentres/report/did-report/view-detail.js.
// Every state hook, useEffect trigger, API call, filter rule, modal, side-panel
// section, and dead-code comment block from v1 is preserved. The only chrome
// changes are:
//   - v1 PrimeReact <DataTable>/<Column> → obd3-gui @/components/data-table
//     (TanStack). Column headers, cell renderers, and paginator row-count of 10
//     are identical. Export builds a 3-sheet Excel (exportDidReport).
//   - v1 PrimeReact <Button label=â€¦> → react-bootstrap <Button> with the same
//     label text. p-button-sm p-button-primary/secondary/outlined classes are
//     dropped in favour of react-bootstrap variants (primary / secondary /
//     outline-secondary / outline-primary).
//   - v1 <Form.Control type="time"> / <Form.Select> from react-bootstrap kept
//     verbatim. Types dropped from imports (@ts-nocheck).
// Bugs preserved verbatim:
//   - `filterValueOptions` deps array omits `filterValue` â€” matches v1.
//   - Empty div-with-onClick backdrop before the modal (line ~1354 of v1) kept.
//   - The `custom-scrollbar-panel` outer container is always rendered, matching
//     v1's translate-hide pattern.
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DataTable } from '@/components/data-table/DataTable';
import type { ColumnDef, Table as TanstackTable } from '@tanstack/react-table';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Inline micro-shim block: replaces `@/lib/rb-shim` Button/Modal/Form.*
// with native form-control markup + shadcn Button/Dialog. Preserves JSX
// (~36 shim sites across 2954 lines) without keeping the bridge dependency.
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
// Side panel is z-1050; nested dialogs (Call Events / IVR / Webhooks) must
// sit above it — same layer as CallEngineTracesDialog.
const NESTED_DIALOG_Z = 'z-[1100]';

const Modal: any = ({ show, onHide, size, centered: _c, children, className, overlayClassName, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        overlayClassName={`${NESTED_DIALOG_Z} ${overlayClassName || ''}`.trim()}
        className={`${sizeCls} ${NESTED_DIALOG_Z} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import {
  ROUTES,
  USER_ROLES,
  BACKEND_API_BASE_URL,
  CAMPAIGN_TYPE_MAP,
  TEMPLATE_TYPE_MAP,
} from '@/utilities/constant';
import { isCallCentreUserRole } from '@/utilities/agentLeadsQuota';
import moment from 'moment';
// v1 imported '@fortawesome/fontawesome-free/css/all.min.css' twice; v2 loads
// FontAwesome globally, so the local import is dropped here.
import {
  DOWNLOAD_REPORT,
  API_INCOMING_REPORT_BY_DID_LIST,
  API_OUTGOING_REPORT_BY_DID_LIST,
  API_HANGUP_CAUSES,
  API_INCOMING_REPORT_TRACE_BATCH,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  Mic,
  Grid3x3,
  PhoneOff,
  GitBranch,
  FileText,
  Clock,
  Phone,
  User,
  Calendar,
  Hourglass,
  Server,
  Network,
  Square,
  CheckCircle2,
  Flag,
  ChevronRight,
  X,
  Layers,
  Megaphone,
  Eye,
  Pause,
  Bot,
  Mic2,
  MapPin,
  Signal,
  Hash,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CallFlowView, { CallLegsTable } from './components/CallFlowView';
import {
  buildCallFlow,
  isAiIvrEventType,
  queueToCallGroup,
  summarizeEventData,
} from './components/callFlowModel';

import { FaInfoCircle } from 'react-icons/fa';
import { toast } from 'sonner';
import {
  loadPortalReportData,
  PORTAL_REPORT_RESOURCES,
} from '@/utilities/portalReportRefresh';
import { Storage } from '@/utilities/setting';
import {
  isChannelLicenceShortageHangup,
  maskIfEnabled,
  maskNumberForHangup,
  maskReportRowNumbers,
} from '@/utilities/cliDisplay';
import { fetchCdrMediaDetails, getRecordingData, hasCdrMediaReference, parseCdrMediaReference } from '@/utilities/cdrMedia';
import { canReadRecordings, hasVisibleCdrMedia } from '@/utilities/recordingsAcl';
import CallRecordingCell from '@/components/partials/components/CallRecordingCell';
import CdrMediaDetails from '@/components/partials/components/CdrMediaDetails';
import MediaAudioPlayer from '@/components/partials/components/MediaAudioPlayer';
import { fetchMediaAudioUrl, revokeMediaAudioUrl } from '@/utilities/mediaAudio';
import CallQualityDetail from './components/CallQualityDetail';
import {
  CallEngineTracesButton,
  CallEngineTracesDialog,
} from './components/CallEngineTraces';
import {
  CallTranscriptButton,
  CallTranscriptDialog,
  useTranscriptAvailability,
} from '@/features/transcripts/CallTranscript';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import BusyGuard from '@/components/feedback/BusyGuard';
import Loader from '@/components/partials/components/Loader';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import { callStatusLabel, isCallConnected } from '@/utilities/callStatus';
import { stampedFileName } from '@/components/data-table/csv-export';
import {
  formatDispositionJson,
  DispositionJsonCell,
} from '@/features/ivr-reports/DispositionJsonCell';

const formatSummaryDateTime = (dateString: any) => {
  if (!dateString) return '-';
  // Report timestamps are already IST with no zone marker — same parse as
  // formatDateTime (the table), so the modal doesn't add a second +5:30.
  const m = moment(String(dateString), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  if (!m.isValid()) return '-';
  return m.format('D MMM YYYY, hh:mm:ss a');
};

const getCallDurationLabel = (call: any) => {
  const start = call?.start_time ? new Date(call.start_time) : null;
  const end = call?.end_time ? new Date(call.end_time) : null;

  let totalSeconds = 0;
  if (call?.call_duration_sec != null) {
    totalSeconds = Number(call.call_duration_sec);
  } else if (call?.total_duration != null && call.total_duration !== '') {
    totalSeconds = Number(call.total_duration);
  } else if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    totalSeconds = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
  } else {
    // Last resort: stored billing_duration (billing only).
    totalSeconds = Number(call?.billing_duration || 0);
  }

  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const CallSummaryCard = ({ icon, iconBg, label, value, valueColor }: any) => (
  <div
    className="shadow-neu-raised flex min-w-0 items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3.5"
  >
    <div
      style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <div
        className="break-words text-sm font-bold text-foreground"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
    </div>
  </div>
);

const CallDetailRow = ({ icon, label, value }: any) => (
  <div
    className="grid items-center border-b border-border/50 py-3.5"
    style={{
      gridTemplateColumns: '32px 150px minmax(0, max-content)',
      columnGap: '20px',
      paddingLeft: '4px',
      paddingRight: '4px',
    }}
  >
    <div className="flex items-center justify-center text-primary">
      {icon}
    </div>
    <div className="text-sm font-semibold text-muted-foreground">
      {label}
    </div>
    <div className="justify-self-start text-left text-sm text-foreground">
      {value}
    </div>
  </div>
);

export const parseIvrJourney = (ivrJourneyRaw: any) => {
  try {
    if (ivrJourneyRaw == null || ivrJourneyRaw === '') return [];

    const parsed =
      typeof ivrJourneyRaw === 'string'
        ? JSON.parse(ivrJourneyRaw)
        : ivrJourneyRaw;

    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean);
  } catch (err) {
    console.log('IVR journey parse error:', err);
    return [];
  }
};

export const parseCallWebhooks = (webhooksRaw: any) => {
  try {
    if (webhooksRaw == null || webhooksRaw === '') return [];

    const parsed =
      typeof webhooksRaw === 'string' ? JSON.parse(webhooksRaw) : webhooksRaw;

    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean);
  } catch (err) {
    console.log('Webhooks parse error:', err);
    return [];
  }
};

export const getWebhooksForCalls = (mainCall: any, relatedCalls: any = []) => {
  const candidates = [mainCall, ...relatedCalls].filter(Boolean);
  for (const call of candidates) {
    const hooks = parseCallWebhooks(call?.webhooks);
    if (hooks.length > 0) return hooks;
  }
  return [];
};

const formatWebhookType = (hook: any) => {
  const type = String(hook?.type || 'WEBHOOK').replace(/_/g, ' ');
  const eventType = hook?.eventType ? String(hook.eventType).replace(/_/g, ' ') : '';
  return eventType ? `${type} • ${eventType}` : type;
};

const parseIvrEventData = (data: any) => {
  if (data == null || data === '') return {};
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

export const getIvrJourneyForCalls = (mainCall: any, relatedCalls: any = []) => {
  const candidates = [mainCall, ...relatedCalls].filter(Boolean);
  for (const call of candidates) {
    const journey = parseIvrJourney(call?.ivr_journey);
    if (journey.length > 0) return journey;
  }
  return [];
};

const formatIvrJourneyTime = (dateString: any) => {
  if (!dateString) return '-';
  try {
    // IVR engine stamps are UTC. A value with no zone is parsed as the
    // browser's local time, so an IST machine shows the UTC clock unchanged.
    const raw = String(dateString).trim();
    const hasZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const normalized = hasZone
      ? raw
      : (raw.includes('T') ? raw : raw.replace(' ', 'T')) + 'Z';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '-';
  }
};

const getIvrEventVisual = (type: string, data: any = {}) => {
  const status = String(data?.status || '').toUpperCase();

  if (isAiIvrEventType(type)) {
    return {
      tone: 'neutral',
      icon: 'bot',
      cardBg: '#f5f3ff',
      cardBorder: '#c4b5fd',
      iconBg: '#7c3aed',
    };
  }
  if (type === 'IVR_START') {
    return {
      tone: 'success',
      icon: 'play',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'CALL_ENDED' || type === 'HANGUP') {
    return {
      tone: 'success',
      icon: 'phone-off',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'DTMF') {
    const isPrompting = status === 'PROMPTING' || data.digit == null || data.digit === '';
    return {
      tone: 'input',
      icon: isPrompting ? 'mic' : 'keypad',
      cardBg: '#fff1f2',
      cardBorder: '#fecaca',
      iconBg: '#ef4444',
    };
  }
  if (type === 'PLAY') {
    return {
      tone: 'success',
      icon: 'play',
      cardBg: '#f0fdf4',
      cardBorder: '#86efac',
      iconBg: '#16a34a',
    };
  }
  if (type === 'TRANSFER') {
    return {
      tone: 'neutral',
      icon: 'phone',
      cardBg: '#eff6ff',
      cardBorder: '#93c5fd',
      iconBg: '#2563eb',
    };
  }
  return {
    tone: 'neutral',
    icon: 'phone',
    cardBg: '#f8fafc',
    cardBorder: '#e2e8f0',
    iconBg: '#64748b',
  };
};

// agentMask: viewer is an agent with number masking on.
export const formatIvrJourneyStep = (event: any, agentMask = false) => {
  const type = String(event?.type || event?.eventname || 'Unknown').toUpperCase();
  const data = parseIvrEventData(event?.data);
  const visual = getIvrEventVisual(type, data);
  const status = String(data?.status || '').toUpperCase();
  let title = queueToCallGroup(type.replace(/_/g, ' '));
  const detailItems: any[] = [];
  let ok = true;

  switch (type) {
    case 'IVR_START':
      title = 'IVR Started';
      if (data.flowName) detailItems.push({ label: 'Flow', value: data.flowName });
      if (data.context) detailItems.push({ label: 'Context', value: data.context });
      if (data.caller) detailItems.push({ label: 'Caller', value: data.caller });
      if (data.did) detailItems.push({ label: 'DID', value: data.did });
      break;
    case 'PLAY':
      title = data.status === 'STARTED' ? 'Playing Audio' : `Play (${data.status || '-'})`;
      if (data.nodeId != null) detailItems.push({ label: 'Node', value: data.nodeId });
      if (data.level != null) detailItems.push({ label: 'Level', value: data.level });
      if (data.audio) detailItems.push({ label: 'Audio', value: data.audio, isLink: true });
      break;
    case 'DTMF':
      if (status === 'PROMPTING' || data.digit == null || data.digit === '') {
        title = 'DTMF Input - Prompting';
        if (data.status) detailItems.push({ label: 'Status', value: data.status });
        if (data.numDigits != null) detailItems.push({ label: 'Digits expected', value: data.numDigits });
        if (data.menuAudio) detailItems.push({ label: 'Menu', value: data.menuAudio, isLink: true });
        else if (data.audio) detailItems.push({ label: 'Audio', value: data.audio, isLink: true });
      } else {
        title = 'DTMF Input';
        detailItems.push({ label: 'Digits', value: data.digit });
        if (data.status) detailItems.push({ label: 'Status', value: data.status });
      }
      ok = !data.status || status === 'VALID';
      break;
    case 'TRANSFER':
      title = data.transferType
        ? `Transfer: ${queueToCallGroup(String(data.transferType).replace(/_/g, ' '))}`
        : 'Transfer';
      if (data.transferName) detailItems.push({ label: 'Target', value: data.transferName });
      if (data.status) detailItems.push({ label: 'Status', value: data.status });
      if (data.slotStartTime && data.slotEndTime) {
        detailItems.push({
          label: 'Slot',
          value: `${data.slotStartTime} - ${data.slotEndTime}`,
        });
      }
      ok = !data.status || !['FAILED', 'NO_SLOT', 'REJECTED'].includes(status);
      break;
    case 'HANGUP':
      title = 'Hangup';
      detailItems.push(...summarizeEventData(data));
      break;
    case 'CALL_ENDED':
      title = 'Call Ended';
      if (data.status) detailItems.push({ label: 'Status', value: data.status });
      if (data.context) detailItems.push({ label: 'Context', value: data.context });
      break;
    default:
      // AI / unknown nodes carry whole JSON payloads — show only the outcome
      // fields (reason, intent, result, ...), never the raw object.
      if (isAiIvrEventType(type)) title = 'AI Agent';
      detailItems.push(...summarizeEventData(data));
      ok = !['FAILED', 'ERROR'].includes(status);
  }

  if (agentMask) {
    // Caller / DID / transfer target and any phone-like value in summarised data.
    detailItems.forEach((item) => {
      if (!item.isLink && /^\+?\d{7,15}$/.test(String(item.value ?? '').replace(/[\s-]/g, ''))) {
        item.value = maskIfEnabled(true, item.value);
      }
    });
  }

  return {
    key: `${type}-${event?.time || event?.createdAt || Math.random()}`,
    type,
    title,
    detailItems,
    rawData: data,
    ok,
    visual,
    time: event?.time || event?.createdAt,
  };
};

const renderIvrEventIcon = (iconName: string, size = 16) => {
  const props: any = { size, color: '#ffffff', strokeWidth: 2.5 };
  switch (iconName) {
    case 'play':
      return <Play {...props} />;
    case 'mic':
      return <Mic {...props} />;
    case 'keypad':
      return <Grid3x3 {...props} />;
    case 'phone-off':
      return <PhoneOff {...props} />;
    case 'bot':
      return <Bot {...props} />;
    default:
      return <Phone {...props} />;
  }
};

const renderIvrDetailItems = (items: any[] = []) => {
  if (!items.length) return null;
  return (
    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
      {items.map((item, idx) => (
        <span key={`${item.label}-${idx}`}>
          {idx > 0 ? ' • ' : ''}
          <span style={{ fontWeight: 600, color: '#334155' }}>{item.label}:</span>{' '}
          {item.isLink ? (
            <MediaAudioPlayer
              src={item.value}
              style={{ height: '30px', verticalAlign: 'middle', display: 'inline-block' }}
            />
          ) : (
            <span>{item.value}</span>
          )}
        </span>
      ))}
    </div>
  );
};


export const IvrJourneyPanel = ({ mainCall, steps, agentMask = false }: any) => (
  <div>
    <div
      style={{
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        marginBottom: '18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '999px',
          background: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Phone size={18} color="#ffffff" strokeWidth={2.5} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>
          {maskNumberForHangup(
            mainCall.caller_number || '-',
            mainCall.hangup_cause,
            agentMask
          )}{' '}
          →{' '}
          {maskNumberForHangup(
            mainCall.callee_number || '-',
            mainCall.hangup_cause,
            agentMask
          )}
        </div>
        <div
          style={{
            fontSize: '12px',
            marginTop: '4px',
            color: '#64748b',
            wordBreak: 'break-word',
          }}
        >
          Call SID: {mainCall.call_sid || '-'} • Trace ID: {mainCall.trace_id || '-'}
          {mainCall.ivr_name ? ` • IVR: ${mainCall.ivr_name}` : ''}
        </div>
      </div>
    </div>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FileText size={16} color="#64748b" />
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
          IVR Event Timeline
        </span>
      </div>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#2563eb',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '999px',
          padding: '4px 10px',
        }}
      >
        {steps.length} Events
      </span>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {steps.map((step: any, index: number) => (
        <div key={`${step.key}-${index}`} style={{ display: 'flex', gap: '14px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '34px',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '999px',
                background: step.visual.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(15,23,42,0.12)',
              }}
            >
              {renderIvrEventIcon(step.visual.icon)}
            </div>
            {index < steps.length - 1 ? (
              <div
                style={{
                  width: '2px',
                  flex: 1,
                  minHeight: '18px',
                  background: '#cbd5e1',
                  marginTop: '6px',
                }}
              />
            ) : null}
          </div>

          <div
            style={{
              flex: 1,
              marginBottom: index < steps.length - 1 ? '12px' : 0,
              padding: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${step.visual.cardBorder}`,
              background: step.visual.cardBg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
                {step.title}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Clock size={13} />
                {formatIvrJourneyTime(step.time)}
              </div>
            </div>

            {step.detailItems.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {renderIvrDetailItems(step.detailItems)}
              </div>
            )}

          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Webhook deliveries for one call — shared with the CDR report call dialog. */
export const WebhooksPanel = ({ events }: any) => (
  <div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FileText size={16} color="#64748b" />
        <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
          Webhook Deliveries
        </span>
      </div>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#0284c7',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '999px',
          padding: '4px 10px',
        }}
      >
        {events.length} Events
      </span>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {events.map((hook: any, index: number) => {
        const success = hook?.success === true || hook?.success === 'true';
        return (
          <div
            key={`${hook?.webhookRequestId || 'hook'}-${index}`}
            style={{
              padding: '12px 14px',
              borderRadius: '12px',
              border: `1px solid ${success ? '#86efac' : '#fecaca'}`,
              background: success ? '#f0fdf4' : '#fff1f2',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: 0,
                }}
              >
                {success ? (
                  <CheckCircle2 size={16} color="#16a34a" />
                ) : (
                  <X size={16} color="#dc2626" />
                )}
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#0f172a',
                    wordBreak: 'break-word',
                  }}
                >
                  {formatWebhookType(hook)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Clock size={13} />
                {formatIvrJourneyTime(hook?.createdAt)}
              </div>
            </div>

            <div
              style={{
                marginTop: '8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                fontSize: '12px',
                color: '#475569',
              }}
            >
              <span
                style={{
                  padding: '3px 9px',
                  borderRadius: '999px',
                  background: success ? '#dcfce7' : '#fee2e2',
                  color: success ? '#15803d' : '#b91c1c',
                  fontWeight: 700,
                }}
              >
                {success ? 'DELIVERED' : 'FAILED'}
              </span>
              {hook?.webhookRequestId != null &&
              hook?.webhookRequestId !== '' ? (
                <span
                  style={{
                    padding: '3px 9px',
                    borderRadius: '999px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  Request ID: {hook.webhookRequestId}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '-';

  // Report timestamps are stored already in IST, with no zone marker. Parsing
  // them as UTC and rendering in Asia/Kolkata added a second +5:30.
  const m = moment(String(dateString), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  if (!m.isValid()) return '-';
  return m.format('D/M/YYYY, HH:mm:ss');
};

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const timeToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const h = String(dt.getHours()).padStart(2, '0');
  const m = String(dt.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

const inputStrToTimeDate = (str: string) => {
  if (!str) return null;
  const [h, m] = str.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const CALL_FILTER_CATEGORIES = [
  { key: 'callee_number', label: 'Callee' },
  { key: 'agent_name', label: 'Agent Name' },
  { key: 'agent_number', label: 'Agent Number' },
  { key: 'caller_number', label: 'Caller' },
  { key: 'status', label: 'Status' },
  { key: 'call_duration', label: 'Customer Duration' },
  { key: 'call_group', label: 'Call Group' },
  { key: 'ivr_name', label: 'IVR Name' },
  { key: 'call_status', label: 'Call Status' },
];

const getReportLegType = (row: any) => {
  const value = row?.leg_type;
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }
  return String(value).trim();
};

const formatReportLegType = (legType: any) => {
  const normalized = String(legType || '').trim().toUpperCase();
  if (!normalized) return null;
  if (normalized === 'AI' || normalized === 'AI_AGENT') return 'AI Agent';
  if (normalized.includes('WEBRTC')) return 'WEBRTC';
  if (normalized === 'MOBILE' || normalized.includes('MOBILE')) return 'Mobile';
  return String(legType).trim();
};

const reportLegTypeLabel = (row: any) =>
  formatReportLegType(getReportLegType(row)) ||
  (String(row?.call_leg || '').toUpperCase() === 'A_PARTY' ? 'Mobile' : '-');

const parseReportMetaData = (row: any) => {
  let raw = row?.meta_data;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw;
};

export const getAiReportMeta = (mainCall: any, relatedCalls: any[] = []) => {
  const rows = [mainCall, ...(Array.isArray(relatedCalls) ? relatedCalls : [])];
  for (const row of rows) {
    const meta = parseReportMetaData(row);
    if (meta?.aiNotes) {
      return {
        aiNotes: meta.aiNotes,
        aiAgentId: meta.aiAgentId || row?.ai_agent_id || null,
      };
    }
  }
  const withId = rows.find((row) => row?.ai_agent_id || parseReportMetaData(row)?.aiAgentId);
  if (!withId) return null;
  const meta = parseReportMetaData(withId);
  return {
    aiNotes: null,
    aiAgentId: meta?.aiAgentId || withId.ai_agent_id || null,
  };
};

const rowsHaveLegType = (rows: any[] = []) =>
  rows.some((row) => Boolean(getReportLegType(row)));

export const getCallInfoLegType = (mainCall: any, relatedCalls: any[] = []) => {
  const mainLeg = getReportLegType(mainCall);
  if (mainLeg) return formatReportLegType(mainLeg);

  const outboundLeg = relatedCalls.find(
    (call) =>
      call?.trace_id === mainCall?.trace_id &&
      call?.call_sid !== mainCall?.call_sid &&
      getReportLegType(call)
  );

  return outboundLeg ? formatReportLegType(getReportLegType(outboundLeg)) : null;
};

const CALL_DURATION_BUCKETS = [
  { label: '0 sec', match: (seconds: number) => seconds === 0 },
  { label: '1-30 sec', match: (seconds: number) => seconds >= 1 && seconds <= 30 },
  { label: '31-120 sec', match: (seconds: number) => seconds >= 31 && seconds <= 120 },
  { label: '120+ sec', match: (seconds: number) => seconds > 120 },
];

const getCallGroupLabel = (row: any) => {
  const value = row?.queue_name || row?.call_group_name || "-";
  return value && String(value).trim() ? String(value).trim() : '';
};

// Customer patch duration = A_PARTY leg's patch_duration (0 if missing).
const customerDuration = (leg: any) => Number(leg?.patch_duration || 0);

// Seconds that decide CONNECTED/FAILED: customer talk time for the
// customer leg, raw billing for any other leg.
const connectedSeconds = (row: any) =>
  row?.customer_duration ??
  (String(row?.call_leg || '').toUpperCase() === 'A_PARTY'
    ? customerDuration(row)
    : Number(row?.billing_duration || 0));

// Stored CDR call_status wins; older rows without it keep the seconds rule.
const rowConnected = (row: any) => isCallConnected(row, Number(connectedSeconds(row)) > 0);
const getConnectionStatus = (row: any) => callStatusLabel(row, rowConnected(row));

const getHangupLabel = (row: any, hangupCauseMap: any = {}) => {
  const code = row?.hangup_cause;
  if (!code) return '';
  return hangupCauseMap[code] || code;
};

const applyCallLevelFilters = (rows: any[], category: string, value: string, hangupCauseMap: any = {}) => {
  if (!category || !value) return rows;

  return rows.filter((row) => {
    switch (category) {
      case 'caller_number':
      case 'callee_number':
      case 'agent_name':
      case 'agent_number':
        return String(row?.[category] || '').trim() === value;
      case 'status':
        return getConnectionStatus(row) === value;
      case 'call_duration': {
        const bucket = CALL_DURATION_BUCKETS.find((item) => item.label === value);
        if (!bucket) return true;
        return bucket.match(Number(row?.customer_duration ?? customerDuration(row)));
      }
      case 'call_group':
        return getCallGroupLabel(row) === value;
      case 'call_status':
        return getHangupLabel(row, hangupCauseMap) === value;
      default:
        return true;
    }
  });
};

const mergeDidReportRows = (primary = [], extra = []) => {
  const out = [];
  const seen = new Set();
  for (const row of [...(Array.isArray(primary) ? primary : []), ...(Array.isArray(extra) ? extra : [])]) {
    if (!row) continue;
    const key = String(
      row.call_sid ||
        row.id ||
        `${row.trace_id || ''}|${row.start_time || ''}|${row.caller_number || ''}|${row.callee_number || ''}`,
    );
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  out.sort((a, b) => {
    const ta = new Date(String(a?.start_time || '').replace(' ', 'T')).getTime();
    const tb = new Date(String(b?.start_time || '').replace(' ', 'T')).getTime();
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });
  return out;
};

const reportRowTime = (value: any) => {
  const time = new Date(String(value || '').replace(' ', 'T')).getTime();
  return Number.isFinite(time) ? time : null;
};

export const isAgentReportLeg = (row: any) =>
  (row?.agent_id != null && String(row.agent_id).trim() !== '') ||
  String(row?.agent_name || '').trim() !== '' ||
  String(row?.agent_number || '').trim() !== '';

// One list row per call. Legs that share a trace_id are the same call.
// A row with no trace_id stays on its own. The customer leg supplies the
// numbers; agent name and number are copied from the agent leg.
const collapseReportRowsByTrace = (rows: any[] = []) => {
  const groups = new Map<string, any[]>();
  const order: string[] = [];

  rows.forEach((row, index) => {
    if (!row) return;
    const trace = String(row.trace_id || '').trim();
    const key = trace || `__leg__${row.call_sid || row.id || index}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(row);
  });

  const collapsed = order.map((key) => {
    const legs = groups.get(key) || [];
    if (legs.length === 1) {
      return { ...legs[0], customer_duration: connectedSeconds(legs[0]), call_legs: legs };
    }


    const customerLeg =
      legs.find((leg) => String(leg?.call_leg || '').toUpperCase() === 'A_PARTY') ||
      legs.find((leg) => !isAgentReportLeg(leg)) ||
      legs[0];
    const agentLeg =
      legs.find((leg) => leg !== customerLeg && isAgentReportLeg(leg)) ||
      (isAgentReportLeg(customerLeg) ? customerLeg : null);

    return {
      ...customerLeg,
      agent_id: agentLeg?.agent_id ?? customerLeg.agent_id,
      agent_name: agentLeg?.agent_name || customerLeg.agent_name,
      agent_number: agentLeg?.agent_number || customerLeg.agent_number,
      customer_duration: customerDuration(customerLeg),
      call_legs: legs,
    };
  });

  collapsed.sort((a, b) => (reportRowTime(b?.start_time) || 0) - (reportRowTime(a?.start_time) || 0));
  return collapsed;
};

export const uniqueReportLegs = (rows: any[] = []) => {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const row of rows) {
    if (!row) continue;
    const key = String(row.call_sid || row.id || '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
};

const fetchTraceCall = async (
  rowData: any,
  { did, isOutgoing, userRole }: { did: string; isOutgoing: boolean; userRole: any },
) => {
  const start = encodeURIComponent(rowData.start_time);
  const end = encodeURIComponent(rowData.end_time);
  const pageDid = encodeURIComponent(did);
  const trace_id = encodeURIComponent(rowData.trace_id);
  const source = isOutgoing || rowData.report_source === 'outbound' ? 'outbound' : 'incoming';

  const response: any = await getApiCall(
    `${API_INCOMING_REPORT_BY_DID_LIST}/${pageDid}/${start}/${end}/${trace_id}?source=${source}`
  );
  return shapeTraceCall(rowData, response, userRole);
};

// Trace payload ({ incomingReports, outboundReports, call_* timings }) → main leg + related legs.
const shapeTraceCall = (rowData: any, response: any, userRole: any) => {
  const incoming = response?.incomingReports || response?.data?.incomingReports || [];
  const outbound = response?.outboundReports || response?.data?.outboundReports || [];
  const forceMask =
    isChannelLicenceShortageHangup(rowData.hangup_cause) ||
    [...incoming, ...outbound].some((r: any) => isChannelLicenceShortageHangup(r?.hangup_cause));
  const legs = uniqueReportLegs([...incoming, ...outbound]);

  let main: any;
  if (userRole === USER_ROLES.CALL_CENTRE_AGENT) {
    main =
      outbound.find((d: any) => d.id === rowData.id) ||
      outbound.find((d: any) => d.call_sid && d.call_sid === rowData.call_sid) ||
      incoming.find((d: any) => d.id === rowData.id) ||
      incoming.find((d: any) => d.call_sid && d.call_sid === rowData.call_sid) ||
      legs.find((d: any) => !isAgentReportLeg(d)) ||
      legs[0];
  } else {
    main =
      incoming.find((d: any) => d.id === rowData.id) ||
      incoming.find((d: any) => d.call_sid && d.call_sid === rowData.call_sid) ||
      outbound.find((d: any) => d.id === rowData.id) ||
      outbound.find((d: any) => d.call_sid && d.call_sid === rowData.call_sid) ||
      legs.find((d: any) => !isAgentReportLeg(d)) ||
      legs[0];
  }

  const maskedMain = maskReportRowNumbers(main || rowData, { force: forceMask });
  const mainKey = String(maskedMain?.call_sid || maskedMain?.id || '');
  const t = response?.data ?? response ?? {};
  return {
    mainCall: {
      ...maskedMain,
      call_start_time: t.call_start_time ?? null,
      call_end_time: t.call_end_time ?? null,
      call_duration_sec: t.call_duration_sec ?? null,
      call_talk_sec: t.call_talk_sec ?? null,
    },
    relatedCalls: legs
      .filter((row: any) => String(row.call_sid || row.id || '') !== mainKey)
      .map((row: any) => maskReportRowNumbers(row, { force: forceMask })),
  };
};

const TRACE_BATCH_SIZE = 200; // backend MAX_TRACE_BATCH (trace-batch endpoints)
const TRACE_BATCH_CONCURRENCY = 2;
const MEDIA_IDS_PER_REQUEST = 50; // backend cdr-media MAX_IDS

type TraceDetail = { mainCall: any; relatedCalls: any[]; media: any[] };

// Recording / voicemail URLs for a batch of exported calls: reference ids from every
// leg are fetched 50 at a time (not once per leg); inline files resolve locally.
const attachExportMedia = async (list: TraceDetail[]) => {
  const recIds = new Set<number>();
  const vmIds = new Set<number>();
  list.forEach((d) =>
    [d.mainCall, ...d.relatedCalls].forEach((leg: any) => {
      const ref = leg?.recording_file ? parseCdrMediaReference(leg.recording_file) : null;
      ref?.recordingIds.forEach((id: number) => recIds.add(id));
      ref?.voicemailIds.forEach((id: number) => vmIds.add(id));
    }),
  );
  const byKey = new Map<string, any>();
  const recs = [...recIds];
  const vms = [...vmIds];
  for (let k = 0; k < Math.max(recs.length, vms.length); k += MEDIA_IDS_PER_REQUEST) {
    const { recordings, voicemails }: any = await fetchCdrMediaDetails({
      mode: 'reference',
      recordings: recs.slice(k, k + MEDIA_IDS_PER_REQUEST),
      voicemails: vms.slice(k, k + MEDIA_IDS_PER_REQUEST),
    });
    [...recordings, ...voicemails].forEach((m: any) =>
      byKey.set(`${m.kind}:${m.id}`, {
        key: `${m.kind}:${m.id}`,
        type: m.kind === 'voicemail' ? 'Voicemail' : 'Recording',
        url: m.audio_url || '',
      }),
    );
  }
  list.forEach((d) => {
    const seen = new Set<string>();
    d.media = [];
    [d.mainCall, ...d.relatedCalls].forEach((leg: any) => {
      if (!leg?.recording_file) return;
      const ref = hasCdrMediaReference(leg.recording_file) ? parseCdrMediaReference(leg.recording_file) : null;
      const items = ref
        ? [
            ...ref.recordingIds.map((id: number) => byKey.get(`recording:${id}`)),
            ...ref.voicemailIds.map((id: number) => byKey.get(`voicemail:${id}`)),
          ].filter(Boolean)
        : getRecordingData(leg.recording_file).map((m: any) => ({
            key: `url:${m.url}`,
            type: String(m.type || '').toLowerCase() === 'voicemail' ? 'Voicemail' : 'Recording',
            url: m.url || '',
          }));
      items.forEach((m: any) => {
        if (seen.has(m.key)) return;
        seen.add(m.key);
        d.media.push(m);
      });
    });
  });
};

/**
 * Calls / Call Legs / Recordings workbook, shared by the DID detail and CDR report
 * exports. Trace details come from a trace-batch endpoint, 200 traces per request,
 * 2 requests in flight, instead of one trace request (+ media requests) per row.
 */
export const exportTraceReport = async ({
  rows,
  batchUrl,
  didFor,
  sourceFor,
  isOutgoingFor,
  userRole,
  hangupCauseMap = {},
  canViewRecordings,
  fileName,
  hideUsername = false,
}: {
  rows: any[];
  batchUrl: string;
  didFor: (row: any) => string;
  sourceFor: (row: any) => 'incoming' | 'outbound';
  isOutgoingFor: (row: any) => boolean;
  userRole: any;
  hangupCauseMap?: any;
  canViewRecordings: boolean;
  fileName: string;
  hideUsername?: boolean;
}) => {
  const hideBilling = isCallCentreUserRole(userRole);
  // Call-centre admin: per-leg pulse values only (parent company's voice plan); no credits.
  const legPulsesOnly = userRole === USER_ROLES.CALL_CENTRE_ADMIN;
  // Super admins see every company's share by name; everyone else gets only their own
  // company's share from the backend, exported as a plain number.
  const showCompanyNames = userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.SUB_SUPERADMIN;
  const toastId = toast.loading(`Exporting 0/${rows.length} calls…`);
  const details: TraceDetail[] = rows.map((row) => ({ mainCall: row, relatedCalls: [], media: [] }));
  const traceIdx = rows.map((r, i) => (r?.trace_id ? i : -1)).filter((i) => i >= 0);
  const chunks: number[][] = [];
  for (let k = 0; k < traceIdx.length; k += TRACE_BATCH_SIZE) chunks.push(traceIdx.slice(k, k + TRACE_BATCH_SIZE));
  let next = 0;
  let done = rows.length - traceIdx.length;
  let failed = 0;

  const worker = async () => {
    while (next < chunks.length) {
      const idxs = chunks[next++];
      const payload = {
        traces: idxs.map((i) => ({ trace_id: rows[i].trace_id, did: didFor(rows[i]), source: sourceFor(rows[i]) })),
      };
      let results: any[] | null = null;
      // ponytail: one retry per chunk for transient failures; if both fail those rows export from list data only.
      for (let attempt = 0; attempt < 2 && !results; attempt++) {
        try {
          const res: any = await postApiCall(batchUrl, payload);
          results = Array.isArray(res) ? res : null;
        } catch {
          /* retry once */
        }
      }
      idxs.forEach((i, k) => {
        if (results?.[k]) details[i] = { ...shapeTraceCall(rows[i], results[k], userRole), media: [] };
        else failed++;
      });
      if (canViewRecordings) await attachExportMedia(idxs.map((i) => details[i]));
      done += idxs.length;
      toast.loading(`Exporting ${done}/${rows.length} calls…`, { id: toastId });
    }
  };

  try {
    if (canViewRecordings && traceIdx.length < rows.length) {
      await attachExportMedia(details.filter((_, i) => !rows[i]?.trace_id));
    }
    await Promise.all(Array.from({ length: Math.min(TRACE_BATCH_CONCURRENCY, chunks.length) }, worker));

    const text = (v: any) => {
      const s = v == null || v === '' || v === false ? '-' : String(v);
      return s.length > 32000 ? `${s.slice(0, 32000)}…` : s; // Excel cell limit
    };
    const at = (v: any) => (!v ? '-' : formatDateTime(v) === '-' ? String(v) : formatDateTime(v));
    // const yn = (v: any) => (v == null ? '-' : v ? 'Yes' : 'No');
    const shares = (list: any[] = []) => {
      if (!list.length) return '-';
      if (showCompanyNames) return list.map((e) => `${e.company}: ${e.value}`).join('; ');
      return Number(list.reduce((sum, e) => sum + (Number(e.value) || 0), 0).toFixed(4));
    };
    const dispositionText = (v: any) => {
      const raw = formatDispositionJson(v);
      if (!raw) return '-';
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return Object.entries(parsed)
            .map(([k, val]) => `${k}: ${val == null || val === '' ? '-' : typeof val === 'object' ? JSON.stringify(val) : String(val)}`)
            .join('; ');
        }
      } catch {
        /* plain text */
      }
      return raw;
    };
    const hasValue = (v: any) => v !== null && v !== undefined && v !== '';
    const numberCol = (row: any, field: string) =>
      maskNumberForHangup(row[field] ?? '-', row.hangup_cause) || '-';

    const callsSheet: any[] = [];
    const legsSheet: any[] = [];
    const mediaSheet: any[] = [];

    rows.forEach((r: any, i: number) => {
      const { mainCall: m, relatedCalls: rel, media } = details[i];
      const steps = getIvrJourneyForCalls(m, rel).map((e: any) => formatIvrJourneyStep(e));
      const flow = buildCallFlow({
        mainCall: m,
        relatedCalls: rel,
        did: didFor(r),
        isOutgoing: isOutgoingFor(r),
        hangupLabel: (code: any) => (code ? hangupCauseMap[code] || '' : ''),
        mask: (value: any) => maskNumberForHangup(value ?? '', m.hangup_cause) || '',
        ivrHasAi: steps.some((s: any) => isAiIvrEventType(s.type)),
      });
      const aParty =
        [m, ...rel].find((leg: any) => String(leg?.call_leg || '').toUpperCase() === 'A_PARTY') || m;
      const ai = getAiReportMeta(m, rel);
      // const notes = ai?.aiNotes || null;

      callsSheet.push({
        'Trace ID': text(r.trace_id),
        'Call SID': text(m.call_sid),
        'Call Start': at(m.call_start_time ?? aParty.start_time),
        'Call End': at(m.call_end_time ?? aParty.end_time),
        'User ID': text(r.user_id),
        ...(hideUsername ? {} : { Username: text(r.user_username) }),
        Caller: numberCol(r, isOutgoingFor(r) ? 'callee_number' : 'caller_number'),
        'Desk Phone': numberCol(r, isOutgoingFor(r) ? 'caller_number' : 'callee_number'),
        'Agent Name': text(r.agent_name),
        'Agent Number': text(maskNumberForHangup(r.agent_number ?? '', r.hangup_cause)),
        'Call Status': getConnectionStatus(r).toUpperCase(),
        'Hangup Reason': text(getHangupLabel(r, hangupCauseMap)),
        'Failure Reason': text(!rowConnected(r) && String(r.call_failure_reason || '').trim()),
        'Call Disconnected By': text(flow.billing.declinedBy),
        'Total Duration': getCallDurationLabel({ ...aParty, call_duration_sec: m.call_duration_sec }),
        'Customer Talk': m.call_talk_sec != null ? getCallDurationLabel({ call_duration_sec: m.call_talk_sec }) : '-',
        'Customer Talk Time (s)': Number(r.patch_duration || 0),
        'Customer Ring (s)': Number(r.bridge_ring_duration || 0),
        'Hold (s)': flow.holdSec,
        'Agents Tried': text(flow.agentsTriedList.map((a) => (a.number ? `${a.name} (${a.number})` : a.name)).join(', ')),
        'Call Group': text(getCallGroupLabel(r)),
        'IVR Name': text(r.ivr_name),
        'Keys Pressed': text(flow.dtmf.join(' → ')),
        'Routing Type': text(queueToCallGroup(m.routing_type)),
        DID: numberCol(m, isOutgoingFor(r) ? 'caller_number' : 'callee_number'),
        'DID ID': text(m.number_id),
        PBX: text(m.pbx_name),
        Trunk: text(m.trunk),
        'Pseudo CDR': Number(m.is_pseudo_cdr) === 1 ? 'Yes' : 'No',
        // Campaign name · #id · type, one column.
        Campaign:
          m.campaign_name || hasValue(m.campaign_id)
            ? [
                m.campaign_name,
                hasValue(m.campaign_id) && `#${m.campaign_id}`,
                hasValue(m.campaign_type) && CAMPAIGN_TYPE_MAP[String(m.campaign_type)],
              ]
                .filter(Boolean)
                .join(' · ')
            : '-',
        'AI Agent': ai?.aiAgentId ? `Agent #${ai.aiAgentId}` : '-',
        // ponytail: AI notes columns hidden for now; uncomment to export them.
        // 'AI Resolved': yn(notes?.resolved),
        // 'AI Satisfied': yn(notes?.satisfied),
        // 'AI Human Requested': yn(notes?.human_requested),
        // 'AI Transfer Attempted': yn(notes?.transfer_attempted),
        // 'AI Conversation Completed': yn(notes?.conversation_completed),
        // 'AI Ended By': text(
        //   notes?.ended_by || notes?.ending_reason
        //     ? `${notes.ended_by || '-'}${notes.ending_reason ? ` — ${notes.ending_reason}` : ''}`
        //     : '',
        // ),
        // 'AI Conversation': text(
        //   notes?.turns != null
        //     ? [
        //         `${notes.turns} turns`,
        //         notes.caller_turns != null && `${notes.caller_turns} from caller`,
        //         notes.duration_sec != null && `${notes.duration_sec}s`,
        //         Number(notes.tool_failures) > 0 && `${notes.tool_failures} tool failures`,
        //       ]
        //         .filter(Boolean)
        //         .join(' · ')
        //     : '',
        // ),
        // 'AI Collected': text(
        //   Object.entries(notes?.collected || {})
        //     .filter(([, v]: any) => v != null && String(v).trim() !== '')
        //     .map(([k, v]) => `${k}: ${v}`)
        //     .join('; '),
        // ),
        // 'AI Still Needed': text((notes?.outstanding || []).map(String).join(', ')),
        // 'AI Actions': text(
        //   (notes?.actions_taken || [])
        //     .map((a: any) => `${a?.ok ? '✓' : '✗'} ${a?.name || '-'}${a?.detail ? ` — ${a.detail}` : ''}`)
        //     .join('; '),
        // ),
        'Disposition Details': text(dispositionText(m.disposition ?? r.disposition)),
        ...(hideBilling
          ? {}
          : { Pulses: shares(flow.billing.pulseTotals), 'Credits Deducted': shares(flow.billing.creditTotals) }),
      });

      const legRow = (sid: string) => (sid ? [m, ...rel].find((x: any) => x?.call_sid === sid) : null);
      flow.billing.legs.forEach((l) => {
        legsSheet.push({
          'Trace ID': text(r.trace_id),
          Leg: text(l.who),
          Role: l.role,
          'Call SID': text(l.callSid),
          'SIP Call ID': text(l.sipCallId),
          'Leg Type': text(l.legType),
          Status: callStatusLabel(legRow(l.callSid) || {}, l.answered),
          Start: at(l.startTime),
          End: at(l.endTime),
          'Ring (s)': l.ringSec || 0,
          'Talk Time (s)': l.talkSec || 0,
          'Hold (s)': l.holdSec || 0,
          'Mute (s)': l.muteSec || 0,
          'Hangup Reason': text(l.hangupReason),
          'Failure Reason': text(l.failureReason),
          Disposition: text(dispositionText(l.disposition)),
          ...(hideBilling
            ? legPulsesOnly
              ? { Pulses: shares(l.pulses) }
              : {}
            : { Pulses: shares(l.pulses), 'Credits Deducted': shares(l.credits) }),
        });
      });

      media.forEach((x: any) => {
        mediaSheet.push({ 'Trace ID': text(r.trace_id), Type: x.type, URL: text(x.url) });
      });
    });

    // Optional columns only when some exported call has a value for them.
    ['IVR Name', 'Keys Pressed', 'AI Agent'].forEach((col) => {
      if (callsSheet.every((c) => c[col] === '-')) callsSheet.forEach((c) => delete c[col]);
    });

    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(callsSheet), 'Calls');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(legsSheet), 'Call Legs');
    if (canViewRecordings) {
      XLSX.utils.book_append_sheet(
        wb,
        mediaSheet.length
          ? XLSX.utils.json_to_sheet(mediaSheet)
          : XLSX.utils.aoa_to_sheet([['Trace ID', 'Type', 'URL']]),
        'Recordings',
      );
    }
    XLSX.writeFile(wb, fileName);
    if (failed) {
      toast.warning(`Exported ${rows.length} calls; ${failed} without full details (load failed)`, { id: toastId });
    } else {
      toast.success(`Exported ${rows.length} calls`, { id: toastId });
    }
  } catch (err: any) {
    console.error(err);
    toast.error(`Export failed${err?.message ? `: ${err.message}` : ''}`, { id: toastId });
  }
};

const DidDetailPage = () => {
  const [hangupCauseMap, setHangupCauseMap] = useState<any>({});
  const canViewRecordings = canReadRecordings();

  const audioRefs = useRef<any>({});
  const audioObjectUrls = useRef<Record<string, string>>({});

  useEffect(
    () => () => {
      Object.values(audioObjectUrls.current).forEach(revokeMediaAudioUrl);
      audioObjectUrls.current = {};
    },
    [],
  );
  const toNum = (value: any) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'incoming';
  const isOutgoingDetail = type === 'outgoing';

  const isConnected = (call: any) => rowConnected(call);
  const hasRung = (call: any) =>
    toNum(call?.bridge_ring_duration) > 0 || toNum(call?.ring_duration) > 0;

  const statusStyles: any = {
    success: {
      backgroundColor: 'rgba(0, 128, 0, 0.7)',
      color: '#ffffff',
      width: '85px',
      padding: '4px 10px',
      borderRadius: '4px',
      textAlign: 'center',
      margin: 0,
    },
    failed: {
      backgroundColor: 'rgba(255, 0, 0, 0.7)',
      color: '#ffffff',
      width: '75px',
      padding: '4px 10px',
      borderRadius: '4px',
      textAlign: 'center',
      margin: 0,
    },
  };

  // Decide Success/Failed from billable duration.
  // Requirement: if `billing_duration > 0` then always show "Success".
  const isSuccessByBillingDuration = (row: any) =>
    Number(row?.billing_duration || 0) > 0;
  const { did, startDate, endDate } = useParams();
  const [rawIncomingReport, setRawIncomingReport] = useState<any[]>([]);
  const [incomingReport, setIncomingReport] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const reportFetchId = useRef(0);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [callFiltersActive, setCallFiltersActive] = useState(false);
  const [detailStartDate, setDetailStartDate] = useState(startDate);
  const [detailEndDate, setDetailEndDate] = useState(endDate);
  const [detailStartTime, setDetailStartTime] = useState(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [detailEndTime, setDetailEndTime] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 0, 0);
    return date;
  });

  const [playingId, setPlayingId] = useState<any>(null);
  const [userRole, setUserRole] = useState<any>(null);
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);

  const [selectedCall, setSelectedCall] = useState<any>([]);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [tracesCallSid, setTracesCallSid] = useState('');
  const [tracesLookupField, setTracesLookupField] = useState('sip_call_id');
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusDialogCall, setStatusDialogCall] = useState<any>(null);
  const [callInfoTab, setCallInfoTab] = useState('flow');

  const [mainCall, setMainCall] = useState<any>(null); // for clicked one
  const [relatedCalls, setRelatedCalls] = useState<any[]>([]); // others with same trace_id

  const ivrJourneyEvents = useMemo(
    () => getIvrJourneyForCalls(mainCall, relatedCalls),
    [mainCall, relatedCalls]
  );
  const hasIvrJourney = ivrJourneyEvents.length > 0;
  const ivrJourneySteps = useMemo(
    () => ivrJourneyEvents.map((event: any) => formatIvrJourneyStep(event)),
    [ivrJourneyEvents]
  );

  const webhookEvents = useMemo(
    () => getWebhooksForCalls(mainCall, relatedCalls),
    [mainCall, relatedCalls]
  );
  const hasWebhooks = webhookEvents.length > 0;

  useEffect(() => {
    if (showSidePanel) setCallInfoTab('flow');
  }, [showSidePanel, mainCall]);

  const callFlow = useMemo(
    () =>
      mainCall
        ? buildCallFlow({
            mainCall,
            relatedCalls,
            did,
            isOutgoing: isOutgoingDetail,
            hangupLabel: (code: any) => (code ? hangupCauseMap[code] || '' : ''),
            mask: (value: any) =>
              maskNumberForHangup(value ?? '', mainCall.hangup_cause) || '',
            ivrHasAi: ivrJourneySteps.some((step: any) => isAiIvrEventType(step.type)),
          })
        : null,
    [mainCall, relatedCalls, did, isOutgoingDetail, hangupCauseMap, ivrJourneySteps]
  );

  const openEngineTraces = useCallback((sid: string, field?: string) => {
    const next = String(sid || '').trim();
    if (!next) return;
    setTracesCallSid(next);
    setTracesLookupField(field === 'call_sid' ? 'call_sid' : 'sip_call_id');
  }, []);

  // One batched availability lookup for the loaded rows, not one per row. The
  // side panel's calls come from these same rows, so it is covered too.
  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => incomingReport.map((r: any) => r?.trace_id), [incomingReport])
  );
  // Lifted like the traces dialog: the Call Information modal is not a radix
  // Dialog, so a nested one would land underneath it.
  const [transcriptTraceId, setTranscriptTraceId] = useState('');
  const openTranscript = useCallback((trace: string) => {
    const next = String(trace || '').trim();
    if (next) setTranscriptTraceId(next);
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User data not found in Storage');
        return;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in Storage user:', e);
        return;
      }
      setUserRole(user?.role);
      setLoggedInUserId(user?.id);
    };
    fetchUserData();
  }, []);

  const fetchIncomingReport = async (
    start: any,
    end: any,
    startTime: any,
    endTime: any,
    force = false
  ) => {
    if (!loggedInUserId || !userRole) {
      return;
    }

    const startMoment = moment(start);
    const endMoment = moment(end);

    const startTimeMoment = moment(startTime);
    const endTimeMoment = moment(endTime);

    const formattedStart = startMoment
      .hour(startTimeMoment.hour())
      .minute(startTimeMoment.minute())
      .second(0)
      .format('YYYY-MM-DD HH:mm:ss');

    const formattedEnd = endMoment
      .hour(endTimeMoment.hour())
      .minute(endTimeMoment.minute())
      .second(59)
      .format('YYYY-MM-DD HH:mm:ss');

    const fetchId = ++reportFetchId.current;
    setLoading(true);
    try {
      const result = await loadPortalReportData({
        resource: isOutgoingDetail
          ? PORTAL_REPORT_RESOURCES.OUTGOING_DID_DETAIL
          : PORTAL_REPORT_RESOURCES.INCOMING_REPORT_BY_DID,
        userId: loggedInUserId,
        role: userRole,
        params: { did, startDate: formattedStart, endDate: formattedEnd },
        force,
        fetchFn: async () => {
          const url = isOutgoingDetail
            ? `${API_OUTGOING_REPORT_BY_DID_LIST}/${did}/${formattedStart}/${formattedEnd}`
            : `${API_INCOMING_REPORT_BY_DID_LIST}/${did}/${formattedStart}/${formattedEnd}`;
          const rows = await getApiCall(url);
          return Array.isArray(rows) ? rows : rows?.data || [];
        },
      });
      if (fetchId !== reportFetchId.current) return;
      const data = collapseReportRowsByTrace(result.data || []);
      setRawIncomingReport(data);
      setIncomingReport(
        callFiltersActive && filterCategory && filterValue
          ? applyCallLevelFilters(data, filterCategory, filterValue, hangupCauseMap)
          : data
      );
    } catch (err) {
      if (fetchId !== reportFetchId.current) return;
      console.log(err);
      setRawIncomingReport([]);
      setIncomingReport([]);
    } finally {
      if (fetchId === reportFetchId.current) setLoading(false);
    }
  };

  const filterValueOptions = useMemo(() => {
    if (!filterCategory) return [];

    if (filterCategory === 'status') {
      return ['Connected', 'Failed', 'Abandoned'];
    }

    if (filterCategory === 'call_duration') {
      return CALL_DURATION_BUCKETS.map((bucket) => bucket.label);
    }

    if (filterCategory === 'call_status') {
      const fromData = rawIncomingReport
        .map((row) => getHangupLabel(row, hangupCauseMap))
        .filter(Boolean);
      const fromApi = Object.values(hangupCauseMap).filter(Boolean) as string[];
      return [...new Set([...fromData, ...fromApi])].sort((a: any, b: any) =>
        a.localeCompare(b)
      );
    }

    if (filterCategory === 'call_group') {
      return [
        ...new Set(
          rawIncomingReport.map((row) => getCallGroupLabel(row)).filter(Boolean)
        ),
      ].sort((a: any, b: any) => a.localeCompare(b));
    }

    return [
      ...new Set(
        rawIncomingReport
          .map((row) => String(row?.[filterCategory] || '').trim())
          .filter(Boolean)
      ),
    ].sort((a: any, b: any) => a.localeCompare(b));
  }, [filterCategory, rawIncomingReport, hangupCauseMap]);

  const handleCallLevelFilterSearch = useCallback(() => {
    if (!filterCategory) {
      toast.info('Select a filter category.');
      return;
    }
    if (!filterValue) {
      toast.info('Select a filter value.');
      return;
    }
    setCallFiltersActive(true);
    setIncomingReport(
      applyCallLevelFilters(rawIncomingReport, filterCategory, filterValue, hangupCauseMap)
    );
  }, [rawIncomingReport, filterCategory, filterValue, hangupCauseMap]);

  const handleClearCallLevelFilter = useCallback(() => {
    setFilterCategory('');
    setFilterValue('');
    setCallFiltersActive(false);
    setIncomingReport(rawIncomingReport);
  }, [rawIncomingReport]);

  useEffect(() => {
    if (!loggedInUserId || !userRole) {
      return;
    }

    fetchIncomingReport(
      detailStartDate,
      detailEndDate,
      detailStartTime,
      detailEndTime,
      false
    );
  }, [
    did,
    type,
    detailStartDate,
    detailEndDate,
    detailStartTime,
    detailEndTime,
    loggedInUserId,
    userRole,
  ]);

  const handleDetailFilter = () => {
    fetchIncomingReport(
      detailStartDate,
      detailEndDate,
      detailStartTime,
      detailEndTime,
      true
    );
  };

  const handleDetailClearFilter = () => {
    setDetailStartDate(startDate);
    setDetailEndDate(endDate);
    const resetStart = new Date();
    resetStart.setHours(0, 0, 0, 0);
    const resetEnd = new Date();
    resetEnd.setHours(23, 59, 0, 0);
    setDetailStartTime(resetStart);
    setDetailEndTime(resetEnd);
    setFilterCategory('');
    setFilterValue('');
    setCallFiltersActive(false);
    fetchIncomingReport(startDate, endDate, resetStart, resetEnd, true);
  };

  const refreshIncomingReport = useCallback(
    (force = false) =>
      fetchIncomingReport(
        detailStartDate,
        detailEndDate,
        detailStartTime,
        detailEndTime,
        force
      ),
    [
      detailStartDate,
      detailEndDate,
      detailStartTime,
      detailEndTime,
      loggedInUserId,
      userRole,
      did,
    ]
  );


  const handlePlayPause = async (id: any, mediaUrl: any) => {
    const current = audioRefs.current[id];
    if (!current) return;

    if (playingId && playingId !== id) {
      audioRefs.current[playingId]?.pause();
      if (audioRefs.current[playingId]) audioRefs.current[playingId].currentTime = 0;
    }

    if (playingId === id) {
      current.pause();
      setPlayingId(null);
      return;
    }

    if (!audioObjectUrls.current[id]) {
      try {
        audioObjectUrls.current[id] = await fetchMediaAudioUrl(mediaUrl);
      } catch (err: any) {
        toast.error(err?.message || 'Unable to play recording');
        return;
      }
    }

    if (current.src !== audioObjectUrls.current[id]) {
      current.src = audioObjectUrls.current[id];
    }

    current.play();
    setPlayingId(id);
  };

  const openStatusDialog = (call: any) => {
    setStatusDialogCall(call);
    setStatusDialogVisible(true);
  };

  const statusPill = (call: any, { compact = false }: any = {}) => {
    const connected = isConnected(call);
    const toneClass = connected
      ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
      : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';

    return (
      <div
        className={
          'inline-flex select-none items-center justify-center gap-1.5 rounded-full font-semibold uppercase tracking-wide ring-1 ring-inset ' +
          (compact ? 'px-2.5 py-0.5 text-[11px] ' : 'px-3 py-1 text-xs ') +
          toneClass
        }
      >
        {compact && connected ? (
          <CheckCircle2 size={12} className="text-emerald-700 dark:text-emerald-300" />
        ) : null}
        <span>{getConnectionStatus(call).toUpperCase()}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openStatusDialog(call);
          }}
          className="ml-0.5 flex cursor-pointer items-center border-0 bg-transparent p-0 leading-none opacity-80 hover:opacity-100"
          aria-label="View call events"
          title="View call events"
        >
          <FaInfoCircle size={compact ? 11 : 13} />
        </button>
      </div>
    );
  };

  const actionBodyTemplate = (rowData: any) => {
    const handleViewClick = async () => {
      if (!rowData.trace_id) {
        console.warn('Trace ID is missing');
        return;
      }

      try {
        const { mainCall: main, relatedCalls: related } = await fetchTraceCall(rowData, {
          did: did as string,
          isOutgoing: isOutgoingDetail,
          userRole,
        });
        setMainCall(main);
        setRelatedCalls(related);
        setShowSidePanel(true);
      } catch (err) {
        console.log(err);
      }
    };

    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleViewClick();
          }}
          title="View trace details"
          aria-label="View trace details"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <CallEngineTracesButton
          callSid={rowData.call_sid}
          sipCallId={rowData.sip_call_id}
          className="h-8 w-8"
          onOpen={openEngineTraces}
        />
        <CallTranscriptButton
          traceId={rowData.trace_id}
          lineCount={transcriptLineCount(rowData.trace_id)}
          className="h-8 w-8"
          onOpen={openTranscript}
        />
      </div>
    );
  };

  const mediaBodyTemplate = (rowData: any) => {
    const isMediaReference = hasCdrMediaReference(rowData.recording_file);
    const recordings = isMediaReference ? [] : getRecordingData(rowData.recording_file);
    const hasRecording = recordings.length > 0;
    if (!hasRecording && !hasVisibleCdrMedia(rowData.recording_file)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <div className="flex items-center gap-1">
        {hasRecording ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause(rowData.id, recordings[0]?.url);
              }}
              title={playingId === rowData.id ? 'Pause recording' : 'Play recording'}
              aria-label={playingId === rowData.id ? 'Pause recording' : 'Play recording'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              {playingId === rowData.id ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
            </button>
            {/* src is attached on first play, once fetched with an Authorization header. */}
            <audio ref={(el) => (audioRefs.current[rowData.id] = el)} />
          </>
        ) : null}

        {isMediaReference && (
          <CdrMediaDetails recordingFile={rowData.recording_file} compact />
        )}
      </div>
    );
  };

  const getDTMFValue = (mainCall: any, relatedCalls: any[]) => {
    const all = [
      mainCall,
      ...relatedCalls.filter((c) => c.trace_id === mainCall.trace_id),
    ];

    const dtmfList = all.map((c) => c.dtmf).filter(Boolean);

    if (!dtmfList.length) return '';
    return dtmfList.sort((a: any, b: any) => b.length - a.length)[0];
  };

  const getValidDTMFSet = (mainCall: any, relatedCalls: any[]) => {
    const all = [
      mainCall,
      ...relatedCalls.filter((c) => c.trace_id === mainCall.trace_id),
    ];

    const validList = all.map((c) => c.valid_dtmf).filter(Boolean);

    if (!validList.length) return new Set();
    const combined = validList.sort((a: any, b: any) => b.length - a.length)[0];
    return new Set(combined.split(',').filter(Boolean));
  };

  useEffect(() => {
    const fetchHangupCauses = async () => {
      try {
        const response: any = await getApiCall(API_HANGUP_CAUSES);
        const data = response?.data || response || [];
        const mapped = Object.fromEntries(
          data.map((item: any) => [
            item.hangup_cause_code,
            item.message || item.hangup_cause_reason || 'Unknown',
          ])
        );

        setHangupCauseMap(mapped);
      } catch (err) {
        console.error('Failed to fetch hangup causes', err);
      }
    };

    fetchHangupCauses();
  }, []);

  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);
  const exportDidReport = async (table: TanstackTable<any>) => {
    if (exportingRef.current) {
      toast.info('Export already in progress');
      return;
    }
    const rows = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!rows.length) {
      toast.info('No calls to export');
      return;
    }
    exportingRef.current = true;
    setExporting(true);
    try {
      await exportTraceReport({
        rows,
        batchUrl: API_INCOMING_REPORT_TRACE_BATCH,
        didFor: () => did as string,
        sourceFor: (r) => (isOutgoingDetail || r.report_source === 'outbound' ? 'outbound' : 'incoming'),
        isOutgoingFor: () => isOutgoingDetail,
        userRole,
        hangupCauseMap,
        canViewRecordings,
        fileName: stampedFileName('did', 'xlsx'),
      });
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  const mainReportColumns: ColumnDef<any>[] = [
    {
      id: 'user_details',
      header: 'User Details',
      accessorFn: (row: any) => `${row.user_id ?? ''} ${row.user_username || ''}`,
      cell: ({ row }: any) => (
        <div className="flex flex-col">
          <span>{row.original.user_id ?? '-'}</span>
          {row.original.user_username ? (
            <span className="text-xs text-muted-foreground">{row.original.user_username}</span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: isOutgoingDetail ? 'callee_number' : 'caller_number',
      header: 'Caller',
      cell: ({ row }: any) =>
        maskNumberForHangup(row.original[isOutgoingDetail ? 'callee_number' : 'caller_number'] ?? '-', row.original.hangup_cause) || '-',
    },
    {
      accessorKey: isOutgoingDetail ? 'caller_number' : 'callee_number',
      header: 'Desk Phone',
      cell: ({ row }: any) =>
        maskNumberForHangup(row.original[isOutgoingDetail ? 'caller_number' : 'callee_number'] ?? '-', row.original.hangup_cause) || '-',
    },
    {
      id: 'agent_details',
      header: 'Agent Details',
      accessorFn: (row: any) => `${row.agent_name || ''} ${row.agent_number || ''}`,
      cell: ({ row }: any) => {
        const number = maskNumberForHangup(row.original.agent_number ?? '', row.original.hangup_cause);
        if (!row.original.agent_name && !number) return '-';
        return (
          <div className="flex flex-col">
            <span>{row.original.agent_name || '-'}</span>
            {number ? <span className="text-xs text-muted-foreground">{number}</span> : null}
          </div>
        );
      },
    },
    {
      id: 'call_status',
      header: 'Call Status',
      cell: ({ row }: any) => {
        const call = row.original;
        const reason = getHangupLabel(call, hangupCauseMap);
        const failure = !isConnected(call) && String(call.call_failure_reason || '').trim();
        return (
          <div className="flex flex-col items-start gap-1">
            {statusPill(call)}
            {reason ? <span className="text-xs text-muted-foreground">{reason}</span> : null}
            {failure ? <span className="text-xs font-medium text-rose-600 dark:text-rose-400">{failure}</span> : null}
          </div>
        );
      },
    },
    {
      accessorKey: 'customer_duration',
      header: 'Customer Duration',
      // Customer leg values exactly as stored in ClickHouse; nothing derived here.
      cell: ({ row }: any) => (
        <div className="flex flex-col whitespace-nowrap text-xs">
          <span><span className="text-muted-foreground">Talk Time = </span>{Number(row.original.patch_duration || 0)}s</span>
          <span><span className="text-muted-foreground">Ring Duration = </span>{Number(row.original.bridge_ring_duration || 0)}s</span>
        </div>
      ),
    },
    {
      id: 'call_group',
      header: 'Call Group',
      cell: ({ row }: any) => getCallGroupLabel(row.original) || '-',
    },
    {
      id: 'ivr_name',
      header: 'IVR Name',
      cell: ({ row }: any) => row.original.ivr_name || '-',
    },
    {
      accessorKey: 'start_time',
      header: 'Call Timings',
      cell: ({ row }: any) => (
        <div className="flex flex-col whitespace-nowrap text-xs">
          <span><span className="text-muted-foreground">Start: </span>{formatDateTime(row.original.start_time)}</span>
          <span><span className="text-muted-foreground">End: </span>{formatDateTime(row.original.end_time)}</span>
        </div>
      ),
    },
    {
      id: 'trace_id',
      header: 'Trace ID',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.trace_id || '—'}
        </span>
      ),
    },
    {
      id: 'operator_details',
      header: 'Operator Details',
      accessorFn: (row: any) =>
        [row.circle_series, row.circle_series_name, row.circle_series_operator_name].filter(Boolean).join(' '),
      cell: ({ row }: any) => {
        const { circle_series, circle_series_name, circle_series_operator_name } = row.original;
        if (!circle_series && !circle_series_name && !circle_series_operator_name) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <div className="flex flex-col text-xs">
            <span><span className="text-muted-foreground">Series: </span><span className="font-mono tabular-nums">{circle_series || '—'}</span></span>
            <span><span className="text-muted-foreground">Circle: </span>{circle_series_name || '—'}</span>
            <span><span className="text-muted-foreground">Operator: </span>{circle_series_operator_name || '—'}</span>
          </div>
        );
      },
    },
    ...(canViewRecordings
      ? [{
          id: 'media',
          header: 'Recordings & Voicemails',
          enableSorting: false,
          cell: ({ row }: any) => mediaBodyTemplate(row.original),
        }]
      : []),
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: ({ row }: any) => actionBodyTemplate(row.original),
    },
  ];

  const sidePanelCallsData = useMemo(() => {
    if (!mainCall) return [];
    return [mainCall, ...relatedCalls.filter((c) => c.trace_id === mainCall.trace_id)];
  }, [mainCall, relatedCalls]);

  const sidePanelCallsColumns: ColumnDef<any>[] = useMemo(() => {
    const showLegType = rowsHaveLegType(sidePanelCallsData);
    const cols: ColumnDef<any>[] = [
      {
        id: 'direction',
        header: '',
        cell: ({ row }: any) =>
          row.original.call_sid === mainCall?.call_sid ? (
            <PhoneIncoming size={18} color="green" strokeWidth={2.5} />
          ) : (
            <PhoneOutgoing size={18} color="blue" strokeWidth={2.5} />
          ),
      },
      { accessorKey: 'call_sid', header: 'Call SID' },
      {
        accessorKey: 'caller_number',
        header: 'Caller Number',
        cell: ({ row }: any) =>
          maskNumberForHangup(
            row.original.caller_number ?? '-',
            mainCall?.hangup_cause || row.original.hangup_cause
          ) || '-',
      },
      {
        accessorKey: 'callee_number',
        header: 'Callee Number',
        cell: ({ row }: any) =>
          maskNumberForHangup(
            row.original.callee_number ?? '-',
            mainCall?.hangup_cause || row.original.hangup_cause
          ) || '-',
      },
      {
        accessorKey: 'agent_name',
        header: 'Agent Name',
        cell: ({ row }: any) => row.original.agent_name || '-',
      },
      {
        id: 'answer_status',
        header: 'Answer Status',
        cell: ({ row }: any) => statusPill(row.original),
      },
      {
        accessorKey: 'start_time',
        header: 'Start Time',
        cell: ({ row }: any) => formatDateTime(row.original.start_time),
      },
      {
        accessorKey: 'end_time',
        header: 'End Time',
        cell: ({ row }: any) => formatDateTime(row.original.end_time),
      },
      { accessorKey: 'patch_duration', header: 'Talk Time' },
      {
        id: 'disposition',
        header: 'Disposition',
        accessorFn: (row: any) => formatDispositionJson(row.disposition),
        cell: ({ row }: any) => (
          <DispositionJsonCell value={row.original.disposition} />
        ),
      },
    ];
    if (showLegType) {
      cols.push({
        id: 'leg_type',
        header: 'Leg Type',
        cell: ({ row }: any) =>
          reportLegTypeLabel(row.original),
      });
    }
    cols.push(
      {
        id: 'hangup_cause',
        header: 'Hangup Cause',
        cell: ({ row }: any) => {
          const cause = row.original.hangup_cause;
          const label = hangupCauseMap[cause] || `Unknown`;
          const isSuccess = cause == 16;
          return (
            <span style={{ color: isSuccess ? 'green' : 'red' }}>{label}</span>
          );
        },
      },
      {
        id: 'campaign_name',
        header: 'Campaign Name',
        cell: ({ row }: any) => row.original.campaign_name || '-',
      },
      {
        id: 'campaign_type',
        header: 'Campaign Type',
        cell: ({ row }: any) =>
          CAMPAIGN_TYPE_MAP[String(row.original.campaign_type)] || '-',
      },
      {
        id: 'ivr_name',
        header: 'IVR Name',
        cell: ({ row }: any) => row.original.ivr_name || '-',
      },
      {
        id: 'template_name',
        header: 'Template Name',
        cell: ({ row }: any) =>
          TEMPLATE_TYPE_MAP[String(row.original.template_id)] || '-',
      }
    );
    if (userRole === USER_ROLES.SUPERADMIN) {
      cols.push({
        id: 'engine_traces',
        header: 'Engine traces',
        enableSorting: false,
        cell: ({ row }: any) => (
          <CallEngineTracesButton
            callSid={row.original.call_sid}
            sipCallId={row.original.sip_call_id}
            className="h-8 w-8"
            onOpen={openEngineTraces}
          />
        ),
      });
    }
    return cols;
  }, [mainCall, hangupCauseMap, sidePanelCallsData, userRole, openEngineTraces]);

  const relatedCallsColumns: ColumnDef<any>[] = useMemo(() => {
    const showLegType = rowsHaveLegType(relatedCalls);
    const cols: ColumnDef<any>[] = [
      { accessorKey: 'call_sid', header: 'Call SID' },
      {
        accessorKey: 'caller_number',
        header: 'Caller Number',
        cell: ({ row }: any) =>
          maskNumberForHangup(
            row.original.caller_number ?? '-',
            mainCall?.hangup_cause || row.original.hangup_cause
          ) || '-',
      },
      { accessorKey: 'agent_name', header: 'Agent Name' },
      {
        accessorKey: 'agent_number',
        header: 'Agent Number',
        cell: ({ row }: any) =>
          maskNumberForHangup(
            row.original.agent_number ?? '-',
            mainCall?.hangup_cause || row.original.hangup_cause
          ) || '-',
      },
      {
        accessorKey: 'start_time',
        header: 'Start Time',
        cell: ({ row }: any) => formatDateTime(row.original.start_time),
      },
      {
        accessorKey: 'end_time',
        header: 'End Time',
        cell: ({ row }: any) => formatDateTime(row.original.end_time),
      },
      {
        id: 'answer_status',
        header: 'Answer Status',
        cell: ({ row }: any) => statusPill(row.original),
      },
      { accessorKey: 'patch_duration', header: 'Talk Time (sec)' },
      {
        id: 'disposition',
        header: 'Disposition',
        accessorFn: (row: any) => formatDispositionJson(row.disposition),
        cell: ({ row }: any) => (
          <DispositionJsonCell value={row.original.disposition} />
        ),
      },
    ];
    if (showLegType) {
      cols.push({
        id: 'leg_type',
        header: 'Leg Type',
        cell: ({ row }: any) =>
          reportLegTypeLabel(row.original),
      });
    }
    cols.push(
      { accessorKey: 'total_hold_duration', header: 'Hold Duration (sec)' },
      {
        id: 'hangup_cause',
        header: 'Hangup Cause',
        cell: ({ row }: any) => {
          const cause = row.original.hangup_cause;
          const label = hangupCauseMap[cause] || `Unknown`;
          const isSuccess = cause == 16;
          return (
            <span style={{ color: isSuccess ? 'green' : 'red' }}>{label}</span>
          );
        },
      },
      {
        id: 'recording',
        header: 'Recording',
        cell: ({ row }: any) => <CallRecordingCell recordingFile={row.original.recording_file} />,
      }
    );
    return canViewRecordings ? cols : cols.filter((c: any) => c.id !== 'recording');
  }, [mainCall, hangupCauseMap, relatedCalls, canViewRecordings]);

  const ivrJourneyBody =
    mainCall && hasIvrJourney ? <IvrJourneyPanel mainCall={mainCall} steps={ivrJourneySteps} /> : null;

  const webhooksBody = hasWebhooks ? <WebhooksPanel events={webhookEvents} /> : null;

  const exportGuard = (
    <BusyGuard
      active={exporting}
      title="Please wait, your export is in progress"
      message="We're preparing your report. Leaving, refreshing or closing this page right now would cancel the export. You can continue as soon as your download starts. Thank you for your patience."
    />
  );

  if (loading) return <>{exportGuard}<Loader /></>;

  return (
    <FillPage>
      {exportGuard}
      <PageHeader
        title={`DID Detail View (${type === 'outgoing' ? 'Outgoing' : 'Incoming'})`}
        description="Per-DID call breakdown with related calls, media, and quality metrics."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              title={`Refresh ${type === 'outgoing' ? 'outgoing' : 'incoming'} DID report`}
              onClick={() => refreshIncomingReport(true)}
            >
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                navigate(
                  type === 'outgoing'
                    ? `/${ROUTES.IVR.OUTCOMING_SUMMARY}`
                    : `/${ROUTES.IVR.INCOMING_SUMMARY}`,
                )
              }
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        }
      />
      <div className="mt-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <CalendarTimeInput
            value={timeToInputStr(detailStartTime)}
            onChange={(e) =>
              setDetailStartTime(inputStrToTimeDate(e) as any)
            }
            className="input-embossed h-10 w-[140px] shrink-0 rounded-md px-3 text-sm"
            aria-label="Start time"
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">to</span>
          <CalendarTimeInput
            value={timeToInputStr(detailEndTime)}
            onChange={(e) =>
              setDetailEndTime(inputStrToTimeDate(e) as any)
            }
            className="input-embossed h-10 w-[140px] shrink-0 rounded-md px-3 text-sm"
            aria-label="End time"
          />
          <Button size="sm" onClick={handleDetailFilter}>
            Filter
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDetailClearFilter}>
            Reset
          </Button>

          <span
            className="mx-1 hidden h-6 w-px shrink-0 self-center bg-border/60 sm:block"
            aria-hidden="true"
          />

          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setFilterValue('');
            }}
            className="input-embossed h-10 w-[170px] shrink-0 rounded-md px-3 text-sm"
            aria-label="Filter category"
          >
            <option value="">Select category…</option>
            {CALL_FILTER_CATEGORIES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
          <select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            disabled={!filterCategory}
            className="input-embossed h-10 w-[180px] shrink-0 rounded-md px-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Filter value"
          >
            <option value="">Select value…</option>
            {filterValueOptions.map((option: any) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={handleCallLevelFilterSearch}>
            Search
          </Button>
          {(filterCategory || filterValue) && (
            <Button variant="ghost" size="sm" onClick={handleClearCallLevelFilter}>
              Clear filters
            </Button>
          )}
          {rawIncomingReport.length > 0 && (
            <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
              Showing{' '}
              <span className="font-medium text-foreground tabular-nums">
                {incomingReport.length}
              </span>{' '}
              of{' '}
              <span className="font-medium text-foreground tabular-nums">
                {rawIncomingReport.length}
              </span>{' '}
              calls
            </span>
          )}
        </div>
        <div className="scroll-container">
            <DataTable
              tableId="did-detail-main:v2"
              columns={mainReportColumns}
              data={incomingReport}
              pageSize={10}
              emptyTitle="No records"
              onExport={exportDidReport}
            />
          </div>
      </div>
      {/* <ToastNotification
            show_toast={showToast}
            set_show_toast={setShowToast}
            {...toastProps}
          />
          <Modal show={showToastModal} centered onHide={() => setShowToastModal(false)} size="sm">
            <Modal.Body className="text-center">
              <h5>{toastMessage}</h5>
              <LottieAnimation animationData={"https://assets6.lottiefiles.com/packages/lf20_touohxv0.json"} />
            </Modal.Body>
          </Modal> */}

      {showSidePanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 'calc(100vw / var(--app-zoom, 1))',
            height: 'calc(100vh / var(--app-zoom, 1))',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(2px)',
            zIndex: 1045,
          }}
          onClick={() => setShowSidePanel(false)}
        />
      )}

      <Modal
        show={statusDialogVisible}
        onHide={() => setStatusDialogVisible(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <Modal.Title className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
              <FaInfoCircle />
            </span>
            Call Events Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statusDialogCall ? (
            (() => {
              const connected = isConnected(statusDialogCall);
              const isIVR = statusDialogCall?.routing_type === 'IVR';

              let parsedLifeCycle: any[] = [];
              try {
                parsedLifeCycle = statusDialogCall?.life_cycle
                  ? JSON.parse(statusDialogCall.life_cycle)
                  : [];
              } catch (e) {
                console.error('Invalid life_cycle JSON', e);
              }

              const steps = parsedLifeCycle.map((item: any, index: number) => {
                const eventName = item?.event || 'Unknown Event';
                let title = eventName;
                let detail = '';
                let ok = true;
                switch (eventName) {
                  case 'Call Initiated':
                    title = 'Call Initiated';
                    detail = `Start: ${formatDateTime(item.time)}`;
                    break;
                  case 'Call Ringing':
                    title = 'Ring';
                    detail = `Ringing At: ${formatDateTime(item.time)}`;
                    break;
                  case 'Remote End Ringing':
                    title = 'Remote End Ringing';
                    detail = `Remote Ringing At: ${formatDateTime(item.time)}`;
                    break;
                  case 'Call Connected':
                    title = isIVR ? 'Connected in IVR' : 'Connected';
                    detail = `Connected At: ${formatDateTime(item.time)}`;
                    break;
                  case 'Remote End Answered':
                    title = 'Remote End Answered';
                    detail = `Remote Answered At: ${formatDateTime(item.time)}`;
                    break;
                  case 'Dtmf Recieved':
                    title = 'DTMF Received';
                    detail = `Digit: ${item?.data?.Digit || '-'} • Status: ${
                      item?.data?.Status || '-'
                    } • Level: ${item?.data?.Level || '-'}`;
                    ok = item?.data?.Status === 'VALID';
                    break;
                  case 'Call Hungup':
                    title = 'Hangup';
                    detail = `End: ${formatDateTime(item.time)}`;
                    break;
                  default:
                    detail = formatDateTime(item.time);
                }
                return { key: `${eventName}-${index}`, title, ok, detail };
              });

              const statusPillTone = connected
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';

              return (
                <div className="space-y-4">
                  {/* Header card */}
                  <div className="rounded-lg border border-border/60 bg-muted/40 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-foreground">
                          <span className="tabular-nums">
                            {maskNumberForHangup(
                              statusDialogCall.caller_number || '-',
                              statusDialogCall.hangup_cause,
                            )}
                          </span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="tabular-nums">
                            {maskNumberForHangup(
                              statusDialogCall.callee_number || '-',
                              statusDialogCall.hangup_cause,
                            )}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Call SID:{' '}
                          <span className="font-mono">
                            {statusDialogCall.call_sid || '—'}
                          </span>{' '}
                          • Trace ID:{' '}
                          <span className="font-mono">
                            {statusDialogCall.trace_id || '—'}
                          </span>
                        </div>
                      </div>
                      <span
                        className={
                          'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                          statusPillTone
                        }
                      >
                        {getConnectionStatus(statusDialogCall)}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="font-semibold uppercase tracking-wide text-muted-foreground">
                          Start
                        </div>
                        <div className="mt-0.5 tabular-nums text-foreground">
                          {formatDateTime(statusDialogCall.start_time)}
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold uppercase tracking-wide text-muted-foreground">
                          End
                        </div>
                        <div className="mt-0.5 tabular-nums text-foreground">
                          {formatDateTime(statusDialogCall.end_time)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Event timeline */}
                  <div>
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Event Timeline
                    </div>
                    <div>
                      {steps.map((step: any, index: number) => (
                        <div key={step.key} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div
                              className={
                                'h-3.5 w-3.5 shrink-0 rounded-full ring-4 ' +
                                (step.ok
                                  ? 'bg-emerald-500 ring-emerald-500/15 dark:bg-emerald-400 dark:ring-emerald-400/15'
                                  : 'bg-rose-500 ring-rose-500/15 dark:bg-rose-400 dark:ring-rose-400/15')
                              }
                            />
                            {index < steps.length - 1 ? (
                              <div className="mt-1 w-0.5 flex-1 bg-border/60" />
                            ) : null}
                          </div>
                          <div
                            className={
                              'w-full ' + (index < steps.length - 1 ? 'pb-4' : '')
                            }
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-foreground">
                                {step.title}
                              </div>
                              <span
                                className={
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ' +
                                  (step.ok
                                    ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                    : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                                }
                              >
                                {step.ok ? 'OK' : 'FAILED'}
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {step.detail}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null}
        </Modal.Body>
      </Modal>


      <div
        className="custom-scrollbar-panel border border-border/60"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: showSidePanel ? 'translate(-50%, -50%)' : 'translate(-50%, -150%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1050,
          backgroundColor: 'hsl(var(--card))',
          color: 'hsl(var(--foreground))',
          borderRadius: '14px',
          boxShadow:
            '10px 10px 24px rgba(163,177,198,0.5), -10px -10px 24px rgba(255,255,255,0.9)',
          width: 'min(1280px, 94%)',
          maxHeight: '90%',
          overflowY: 'auto',
          padding: '20px',
          display: showSidePanel ? 'block' : 'none',
        }}
      >
        {showSidePanel && (mainCall || relatedCalls.length > 0) && (
          <>
            {/* FIXED CALL INFO SECTION */}

            <div>
              {mainCall &&
                (() => {
                  const legType = getCallInfoLegType(mainCall, relatedCalls);
                  const aiReportMeta = getAiReportMeta(mainCall, relatedCalls);
                  const mainCallMediaReference = hasCdrMediaReference(mainCall.recording_file);
                  const recordings = mainCallMediaReference || !canViewRecordings
                    ? []
                    : getRecordingData(mainCall.recording_file);
                  const connected = isConnected(mainCall);
                  const dtmfValue =
                    mainCall.routing_type === 'IVR'
                      ? getDTMFValue(mainCall, relatedCalls)
                      : '';
                  const validDtmfSet = getValidDTMFSet(mainCall, relatedCalls);
                  const dtmfDigits = dtmfValue.split(',').filter(Boolean);
                  const showDtmfJourney =
                    mainCall.routing_type === 'IVR' && dtmfDigits.length > 0;
                  const aParty =
                    [mainCall, ...relatedCalls].find(
                      (leg: any) => String(leg?.call_leg || '').toUpperCase() === 'A_PARTY',
                    ) || mainCall;
                  const agentLeg = [aParty, mainCall, ...relatedCalls].find(
                    (leg: any) => leg?.agent_name || leg?.agent_number,
                  );
                  const connectedTo = agentLeg
                    ? {
                        type: 'Agent',
                        name: agentLeg.agent_name || '-',
                        sub: maskNumberForHangup(agentLeg.agent_number || '', aParty.hangup_cause),
                      }
                    : aParty.routing_type === 'AI_AGENT' || aiReportMeta?.aiAgentId
                      ? { type: 'AI Agent', name: aiReportMeta?.aiAgentId ? `Agent #${aiReportMeta.aiAgentId}` : '-' }
                      : aParty.ivr_name
                        ? { type: 'IVR', name: aParty.ivr_name }
                        : getCallGroupLabel(aParty) && getCallGroupLabel(aParty) !== '-'
                          ? { type: 'Call Group', name: getCallGroupLabel(aParty) }
                          : null;
                  const dispositionLabel = connected ? 'ANSWERED' : 'NO ANSWER';

                  const detailRows: any[] = [
                    {
                      icon: <Server size={16} />,
                      label: 'Call Sid',
                      value: (
                        <span className="inline-flex items-center gap-2">
                          <span className="break-all">{mainCall.call_sid || '-'}</span>
                          <CallEngineTracesButton
                            callSid={mainCall.call_sid}
                            sipCallId={mainCall.sip_call_id}
                            className="h-7 w-7"
                            onOpen={openEngineTraces}
                          />
                          <CallTranscriptButton
                            traceId={mainCall.trace_id}
                            lineCount={transcriptLineCount(mainCall.trace_id)}
                            className="h-7 w-7"
                            onOpen={openTranscript}
                          />
                        </span>
                      ),
                    },
                    {
                      icon: <MapPin size={16} />,
                      label: 'Circle',
                      value: mainCall.circle_series_name
                        ? `${mainCall.circle_series_name}${mainCall.circle_series ? ` (${mainCall.circle_series})` : ''}`
                        : '-',
                    },
                    {
                      icon: <Signal size={16} />,
                      label: 'Operator',
                      value: mainCall.circle_series_operator_name || '-',
                    },
                    {
                      icon: <Phone size={16} />,
                      label: 'DID',
                      value: maskNumberForHangup(
                        (isOutgoingDetail ? mainCall.caller_number : mainCall.callee_number) || '-',
                        mainCall.hangup_cause
                      ),
                    },
                    {
                      icon: <Hash size={16} />,
                      label: 'DID ID',
                      value: mainCall.number_id ?? '-',
                    },
                    {
                      icon: <Server size={16} />,
                      label: 'PBX',
                      value: mainCall.pbx_name || '-',
                    },
                    {
                      icon: <GitBranch size={16} />,
                      label: 'Routing Type',
                      value: queueToCallGroup(mainCall.routing_type) || '-',
                    },
                    {
                      icon: <Hash size={16} />,
                      label: 'Routing ID',
                      value: mainCall.routing_id || '-',
                    },
                    {
                      icon: <Flag size={16} />,
                      label: 'Pseudo CDR',
                      value: Number(mainCall.is_pseudo_cdr) === 1 ? 'Yes' : 'No',
                    },
                    {
                      icon: <Network size={16} />,
                      label: 'Trunk',
                      value: mainCall.trunk || '-',
                    },
                    // {
                    //   icon: <Flag size={16} className="text-primary" />,
                    //   label: 'Call Disposition',
                    //   value: (
                    //     <span
                    //       className={
                    //         'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ' +
                    //         (connected
                    //           ? 'bg-primary/10 text-primary ring-primary/25'
                    //           : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                    //       }
                    //     >
                    //       {dispositionLabel}
                    //     </span>
                    //   ),
                    // },
                  ];

                  if (mainCall.campaign_id != null && mainCall.campaign_id !== '') {
                    detailRows.push({
                      icon: <Megaphone size={16} />,
                      label: 'Campaign ID',
                      value: mainCall.campaign_id,
                    });
                  }

                  if (mainCall.campaign_name) {
                    detailRows.push({
                      icon: <Megaphone size={16} />,
                      label: 'Campaign Name',
                      value: mainCall.campaign_name,
                    });
                  }

                  if (
                    mainCall.campaign_type !== null &&
                    mainCall.campaign_type !== undefined &&
                    mainCall.campaign_type !== ''
                  ) {
                    const campaignTypeLabel =
                      CAMPAIGN_TYPE_MAP[String(mainCall.campaign_type)];
                    if (campaignTypeLabel) {
                      detailRows.push({
                        icon: <Layers size={16} />,
                        label: 'Campaign Type',
                        value: campaignTypeLabel,
                      });
                    }
                  }

                  if (mainCall.ivr_name) {
                    detailRows.push({
                      icon: <GitBranch size={16} />,
                      label: 'IVR Name',
                      value: mainCall.ivr_name,
                    });
                  }

                  if (
                    mainCall.template_id !== null &&
                    mainCall.template_id !== undefined &&
                    mainCall.template_id !== ''
                  ) {
                    const templateLabel = TEMPLATE_TYPE_MAP[String(mainCall.template_id)];
                    if (templateLabel) {
                      detailRows.push({
                        icon: <FileText size={16} />,
                        label: 'Template Name',
                        value: templateLabel,
                      });
                    }
                  }

                  if (showDtmfJourney) {
                    detailRows.push({
                      icon: <Grid3x3 size={16} />,
                      label: 'DTMF Journey',
                      value: (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {dtmfDigits.map((digit: any, index: number) => {
                            const isValid = validDtmfSet.has(digit);
                            return (
                              <React.Fragment key={index}>
                                <span
                                  className={
                                    'inline-flex h-7 min-w-[28px] items-center justify-center rounded-lg px-1.5 text-xs font-extrabold ring-1 ring-inset ' +
                                    (isValid
                                      ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                      : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                                  }
                                >
                                  {digit}
                                </span>
                                {index < dtmfDigits.length - 1 && (
                                  <span className="text-xs text-muted-foreground">→</span>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      ),
                    });
                  }

                  const legMetas = [mainCall, ...relatedCalls].map(parseReportMetaData).filter(Boolean);
                  const metaValue = (key: string) =>
                    legMetas.find((m: any) => m[key] != null && m[key] !== '')?.[key];
                  const aiNotes = aiReportMeta?.aiNotes || null;
                  const transferredFrom = metaValue('transferredFrom');
                  const transferredTo = metaValue('transferredTo');
                  const associatedCallId = metaValue('associatedCallId');
                  const totalRetries = metaValue('totalRetries');
                  const retryAttempt = Number(metaValue('retryAttempt') || 0);
                  const transferPartyLabel = (t: any) =>
                    !t
                      ? '-'
                      : String(t.type).toUpperCase() === 'AI'
                        ? `AI Agent #${t.aiAgentId || '-'}`
                        : String(t.type).toLowerCase() === 'agent'
                          ? `Agent ${t.agentName || `#${t.agentId || '-'}`}${
                              t.agentNumber ? ` (${maskNumberForHangup(t.agentNumber, mainCall.hangup_cause)})` : ''
                            }`
                          : String(t.type || '-');
                  const yesNoChip = (label: string, value: any, goodWhenTrue = true) =>
                    value == null ? null : (
                      <span
                        key={label}
                        className={
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                          (Boolean(value) === goodWhenTrue
                            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                        }
                      >
                        {label}: {value ? 'Yes' : 'No'}
                      </span>
                    );
                  const collectedEntries = Object.entries(aiNotes?.collected || {}).filter(
                    ([, v]: any) => v != null && String(v).trim() !== '',
                  );
                  const sectionTitleCls = 'mb-2 flex items-center gap-2 text-sm font-semibold text-foreground';
                  const extraInfo = (
                    <>
                      {aiNotes ? (
                        <div className="border-t border-border/50 px-[18px] py-4">
                          <div className={sectionTitleCls}>
                            <Bot size={16} className="text-primary" /> AI Call
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex flex-wrap gap-1.5">
                              {yesNoChip('Resolved', aiNotes.resolved)}
                              {yesNoChip('Satisfied', aiNotes.satisfied)}
                              {yesNoChip('Human requested', aiNotes.human_requested, false)}
                              {yesNoChip('Transfer attempted', aiNotes.transfer_attempted)}
                              {yesNoChip('Conversation completed', aiNotes.conversation_completed)}
                            </div>
                            {aiNotes.ended_by || aiNotes.ending_reason ? (
                              <div>
                                <span className="text-muted-foreground">Ended by: </span>
                                {aiNotes.ended_by || '-'}
                                {aiNotes.ending_reason ? ` — ${aiNotes.ending_reason}` : ''}
                              </div>
                            ) : null}
                            {aiNotes.turns != null ? (
                              <div>
                                <span className="text-muted-foreground">Conversation: </span>
                                {aiNotes.turns} turns
                                {aiNotes.caller_turns != null ? ` · ${aiNotes.caller_turns} from caller` : ''}
                                {aiNotes.duration_sec != null ? ` · ${aiNotes.duration_sec}s` : ''}
                                {Number(aiNotes.tool_failures) > 0 ? ` · ${aiNotes.tool_failures} tool failures` : ''}
                              </div>
                            ) : null}
                            {collectedEntries.length ? (
                              <div>
                                <div className="text-muted-foreground">Collected</div>
                                <div className="mt-1 grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
                                  {collectedEntries.map(([k, v]: any) => (
                                    <div key={k} className="flex gap-2 text-xs">
                                      <span className="text-muted-foreground">{k}:</span>
                                      <span className="font-medium text-foreground">{String(v)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {aiNotes.outstanding?.length ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-muted-foreground">Still needed:</span>
                                {aiNotes.outstanding.map((o: any) => (
                                  <span
                                    key={String(o)}
                                    className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                                  >
                                    {String(o)}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {aiNotes.actions_taken?.length ? (
                              <div>
                                <div className="text-muted-foreground">Actions taken</div>
                                <ul className="mt-1 list-none space-y-0.5 p-0 text-xs">
                                  {aiNotes.actions_taken.map((a: any, i: number) => (
                                    <li key={i}>
                                      <span className={a?.ok ? 'text-emerald-600' : 'text-rose-600'}>{a?.ok ? '✓' : '✗'}</span>{' '}
                                      <span className="font-medium">{a?.name || '-'}</span>
                                      {a?.detail ? <span className="text-muted-foreground"> — {a.detail}</span> : null}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {transferredFrom || transferredTo || associatedCallId ? (
                        <div className="border-t border-border/50 px-[18px] py-4">
                          <div className={sectionTitleCls}>
                            <GitBranch size={16} className="text-primary" /> Transfers
                          </div>
                          <div className="space-y-1 text-sm">
                            {transferredFrom || transferredTo ? (
                              <div>
                                {transferPartyLabel(transferredFrom)} <span className="text-muted-foreground">→</span>{' '}
                                {transferPartyLabel(transferredTo)}
                              </div>
                            ) : null}
                            {associatedCallId ? (
                              <div>
                                <span className="text-muted-foreground">Associated Call SID: </span>
                                <span className="font-mono text-xs">{String(associatedCallId)}</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {hasWebhooks ? (
                        <div className="border-t border-border/50 px-[18px] py-4">
                          <div className={sectionTitleCls}>
                            <Network size={16} className="text-primary" /> Webhooks
                            <button
                              type="button"
                              onClick={() => setCallInfoTab('webhooks')}
                              className="ml-auto cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:underline"
                            >
                              View details →
                            </button>
                          </div>
                          <ul className="m-0 list-none space-y-1 p-0 text-sm">
                            {webhookEvents.map((hook: any, i: number) => {
                              const ok = hook?.success === true || String(hook?.status || '').toLowerCase() === 'success';
                              return (
                                <li key={i} className="flex flex-wrap items-center gap-2">
                                  <span className={ok ? 'text-emerald-600' : 'text-rose-600'}>{ok ? '✓' : '✗'}</span>
                                  <span className="font-medium">{formatWebhookType(hook)}</span>
                                  {hook?.webhookRequestId ? (
                                    <span className="text-xs text-muted-foreground">#{hook.webhookRequestId}</span>
                                  ) : null}
                                  {hook?.createdAt ? (
                                    <span className="text-xs text-muted-foreground">{formatSummaryDateTime(hook.createdAt)}</span>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : null}
                      {totalRetries != null ? (
                        <div className="border-t border-border/50 px-[18px] py-4">
                          <div className={sectionTitleCls}>
                            <Megaphone size={16} className="text-primary" /> Campaign Retry
                          </div>
                          <div className="text-sm">
                            {retryAttempt > 0
                              ? `Retry ${retryAttempt} of ${totalRetries}`
                              : `First attempt (${totalRetries} retries allowed)`}
                          </div>
                        </div>
                      ) : null}
                    </>
                  );

                  const splitIndex = Math.ceil(detailRows.length / 2);
                  const leftRows = detailRows.slice(0, splitIndex);
                  const rightRows = detailRows.slice(splitIndex);

                  const highlightChipCls =
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ';
                  const flowHighlights = (
                    <>
                      {recordings.length > 0 || (mainCallMediaReference && hasVisibleCdrMedia(mainCall.recording_file)) ? (
                        <button
                          type="button"
                          onClick={() => setCallInfoTab('media')}
                          className={highlightChipCls + 'cursor-pointer border-0 bg-emerald-100 text-emerald-800 ring-emerald-300 hover:bg-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300'}
                        >
                          <Mic2 size={12} /> Recording available
                        </button>
                      ) : null}
                      {aiReportMeta?.aiNotes?.sentiment ? (
                        <span className={highlightChipCls + 'bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-950/50 dark:text-violet-300'}>
                          <Bot size={12} /> Sentiment: {String(aiReportMeta.aiNotes.sentiment)}
                        </span>
                      ) : null}
                      {mainCall.campaign_name ? (
                        <span className={highlightChipCls + 'bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-950/50 dark:text-sky-300'}>
                          <Megaphone size={12} /> {mainCall.campaign_name}
                        </span>
                      ) : null}
                    </>
                  );

                  return (
                    <div>
                      <div className="mb-5 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Call Information
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                              <Phone size={20} strokeWidth={2.5} />
                            </span>
                            <h3 className="m-0 flex flex-wrap items-center gap-2 text-2xl font-bold text-foreground tabular-nums">
                              {maskNumberForHangup(
                                mainCall.caller_number || '-',
                                mainCall.hangup_cause,
                              )}
                              <ChevronRight size={20} className="text-muted-foreground" />
                              {maskNumberForHangup(
                                mainCall.callee_number || '-',
                                mainCall.hangup_cause,
                              )}
                            </h3>
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {isOutgoingDetail ? <PhoneOutgoing size={12} /> : <PhoneIncoming size={12} />}
                              {isOutgoingDetail ? 'Outgoing' : 'Incoming'}
                            </span>
                            <span
                              className={
                                'inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ' +
                                (connected
                                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                  : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                              }
                            >
                              {getConnectionStatus(mainCall).toUpperCase()}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>
                              Trace ID: <span className="font-mono text-foreground">{mainCall.trace_id || '—'}</span>
                            </span>
                            <span>·</span>
                            <span>
                              Legs: <span className="font-semibold text-foreground">{sidePanelCallsData.length}</span>
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowSidePanel(false)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label="Close"
                          title="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: '14px',
                          marginBottom: '20px',
                        }}
                      >
                        <CallSummaryCard
                          icon={<User size={18} className="text-primary" />}
                          iconBg="hsl(var(--primary) / 0.10)"
                          label="Connected To"
                          value={
                            connectedTo ? (
                              <div className="flex flex-col gap-0.5">
                                <span>{connectedTo.name}</span>
                                <span className="text-xs font-normal text-muted-foreground">
                                  {connectedTo.type}
                                  {connectedTo.sub ? ` · ${connectedTo.sub}` : ''}
                                </span>
                              </div>
                            ) : (
                              '-'
                            )
                          }
                        />
                        <CallSummaryCard
                          icon={<Calendar size={18} className="text-primary" />}
                          iconBg="hsl(var(--primary) / 0.10)"
                          label="Call Timings"
                          value={
                            <div className="flex flex-col gap-0.5">
                              <span><span className="font-normal text-muted-foreground">Start: </span>{formatSummaryDateTime(mainCall.call_start_time ?? aParty.start_time)}</span>
                              <span><span className="font-normal text-muted-foreground">End: </span>{formatSummaryDateTime(mainCall.call_end_time ?? aParty.end_time)}</span>
                            </div>
                          }
                        />
                        <CallSummaryCard
                          icon={<Hourglass size={18} className="text-primary" />}
                          iconBg="hsl(var(--primary) / 0.10)"
                          label="Call Duration"
                          value={
                            <div className="flex flex-col gap-0.5">
                              <span><span className="font-normal text-muted-foreground">Total: </span>{getCallDurationLabel({ ...aParty, call_duration_sec: mainCall.call_duration_sec })}</span>
                              {mainCall.call_talk_sec != null ? (
                                <span><span className="font-normal text-muted-foreground">Customer Talk: </span>{getCallDurationLabel({ call_duration_sec: mainCall.call_talk_sec })}</span>
                              ) : null}
                            </div>
                          }
                        />
                      </div>

                      <Tabs value={callInfoTab} onValueChange={setCallInfoTab}>
                        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
                          <TabsTrigger value="flow" className="gap-1.5 px-3 py-1.5"><GitBranch size={14} /> Call Flow</TabsTrigger>
                          <TabsTrigger value="details" className="gap-1.5 px-3 py-1.5"><FileText size={14} /> Details</TabsTrigger>
                          <TabsTrigger value="legs" className="gap-1.5 px-3 py-1.5"><Layers size={14} /> Call Legs ({sidePanelCallsData.length})</TabsTrigger>
                          {hasIvrJourney ? (
                          <TabsTrigger value="ivr" className="gap-1.5 px-3 py-1.5"><Grid3x3 size={14} /> IVR Journey ({ivrJourneySteps.length})</TabsTrigger>
                          ) : null}
                          {hasWebhooks ? (
                          <TabsTrigger value="webhooks" className="gap-1.5 px-3 py-1.5"><Network size={14} /> Webhooks ({webhookEvents.length})</TabsTrigger>
                          ) : null}
                          <TabsTrigger value="media" className="gap-1.5 px-3 py-1.5"><Mic2 size={14} /> Recordings & Quality</TabsTrigger>
                        </TabsList>

                        <TabsContent value="flow">
                          {callFlow ? (
                            <CallFlowView flow={callFlow} isOutgoing={isOutgoingDetail} extraHighlights={flowHighlights} hideCredits={isCallCentreUserRole(userRole)} onViewIvrJourney={hasIvrJourney ? () => setCallInfoTab('ivr') : undefined}>
                      {aiReportMeta?.aiNotes ? (
                        <div className="shadow-neu-raised mt-4 overflow-hidden rounded-2xl border border-border/60 bg-card">
                          <div className="border-b border-border/50 px-[18px] py-4 text-[15px] font-bold text-foreground">
                            AI Call Notes
                            {aiReportMeta.aiAgentId ? (
                              <span className="ml-2 text-xs font-medium text-muted-foreground">
                                Agent #{aiReportMeta.aiAgentId}
                              </span>
                            ) : null}
                          </div>
                          <div className="space-y-2 px-[18px] py-4 text-sm">
                            {aiReportMeta.aiNotes.sentiment ? (
                              <div>
                                <span className="font-semibold text-muted-foreground">Sentiment: </span>
                                {String(aiReportMeta.aiNotes.sentiment)}
                                {aiReportMeta.aiNotes.sentiment_reason
                                  ? ` — ${aiReportMeta.aiNotes.sentiment_reason}`
                                  : ''}
                              </div>
                            ) : null}
                            {aiReportMeta.aiNotes.needs_human != null ? (
                              <div>
                                <span className="font-semibold text-muted-foreground">Needs human: </span>
                                {aiReportMeta.aiNotes.needs_human ? 'Yes' : 'No'}
                              </div>
                            ) : null}
                            {aiReportMeta.aiNotes.brief ? (
                              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
                                {String(aiReportMeta.aiNotes.brief)}
                              </pre>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                            </CallFlowView>
                          ) : null}
                        </TabsContent>

                        <TabsContent value="details">
                      <div className="shadow-neu-raised overflow-hidden rounded-2xl border border-border/60 bg-card">
                        <div className="border-b border-border/50 px-[18px] py-4 text-[15px] font-bold text-foreground">
                          Call Details
                        </div>

                        <div
                          style={{
                            padding: '0 16px',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '0 20px',
                          }}
                        >
                          <div>
                            {leftRows.map((row: any, index: number) => (
                              <CallDetailRow
                                key={`left-${row.label}-${index}`}
                                icon={row.icon}
                                label={row.label}
                                value={row.value}
                              />
                            ))}
                          </div>
                          <div>
                            {rightRows.map((row: any, index: number) => (
                              <CallDetailRow
                                key={`right-${row.label}-${index}`}
                                icon={row.icon}
                                label={row.label}
                                value={row.value}
                              />
                            ))}
                          </div>
                        </div>
                        {extraInfo}
                      </div>
                        </TabsContent>

                        <TabsContent value="legs">
                          {callFlow ? (
                            <CallLegsTable billing={callFlow.billing} hideBilling={isCallCentreUserRole(userRole)} />
                          ) : null}
                        </TabsContent>

                        {hasIvrJourney ? <TabsContent value="ivr">{ivrJourneyBody}</TabsContent> : null}
                        {hasWebhooks ? <TabsContent value="webhooks">{webhooksBody}</TabsContent> : null}

                        <TabsContent value="media">
                          {!recordings.length && !(mainCallMediaReference && hasVisibleCdrMedia(mainCall.recording_file)) ? (
                            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                              No recording for this call.
                            </div>
                          ) : null}
                      {/* WebRTC media quality for this call (getStats). The browser
                        keys its record by the SIP session id, which may differ
                        from the CDR call_sid, so every identifier this row
                        carries is tried. Silent for non-WebRTC legs; for a WEBRTC
                        trunk it explains why data is missing instead. */}
                      <CallQualityDetail
                        callSid={mainCall.call_sid}
                        altIds={[
                          mainCall.trace_id,
                          mainCall.channel_id,
                          mainCall.agent_channel_id,
                          mainCall.call_id,
                          ...relatedCalls
                            .filter((c) => c.trace_id === mainCall.trace_id)
                            .map((c) => c.call_sid),
                        ]}
                        showEmptyState={
                          String(mainCall.trunk_type || '').toUpperCase() === 'WEBRTC'
                        }
                      />

                      {mainCallMediaReference && hasVisibleCdrMedia(mainCall.recording_file) && (
                        <div className="mt-4">
                          <div className="mb-2.5 text-sm font-bold text-foreground">
                            Recordings / Voicemails
                          </div>
                          <CdrMediaDetails recordingFile={mainCall.recording_file} />
                        </div>
                      )}

                      {recordings.length > 0 && (
                        <div className="mt-4">
                          <div className="mb-2.5 text-sm font-bold text-foreground">
                            Recordings
                          </div>
                          <div className="flex flex-col gap-2.5">
                            {recordings.map((rec: any, index: number) => (
                              <div
                                key={index}
                                className="rounded-xl border border-border/60 bg-muted/40 p-3"
                              >
                                <MediaAudioPlayer
                                  src={rec.url}
                                  style={{ width: '100%', height: '35px' }}
                                />
                                <div
                                  className="mt-2 grid gap-1 text-xs text-muted-foreground"
                                  style={{ gridTemplateColumns: '100px auto' }}
                                >
                                  <b className="text-foreground">Name:</b> <span>{rec.name}</span>
                                  <b className="text-foreground">Duration:</b>{' '}
                                  <span>{rec.duration ?? 0} sec</span>
                                  <b className="text-foreground">Status:</b>{' '}
                                  <span>{rec.status}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                        </TabsContent>
                      </Tabs>
                    </div>
                  );
                })()}
            </div>
          </>
        )}
      </div>
      <CallEngineTracesDialog
        open={Boolean(tracesCallSid)}
        onOpenChange={(next) => {
          if (!next) setTracesCallSid('');
        }}
        callSid={tracesCallSid}
        lookupField={tracesLookupField}
        preventOutsideClose={showSidePanel}
      />
      <CallTranscriptDialog
        open={Boolean(transcriptTraceId)}
        onOpenChange={(next) => {
          if (!next) setTranscriptTraceId('');
        }}
        traceId={transcriptTraceId}
        preventOutsideClose={showSidePanel}
      />
    </FillPage>
  );
};

export default DidDetailPage;
